/**
 * 全局音频管理器
 * 负责管理全局音频资源和设备切换
 * 
 * 设计特点：
 * - 单例模式，全局唯一实例
 * - 使用用户中心化的WebSocket架构
 * - 负责设备间的智能切换
 * - 维护全局音频上下文
 */

import { MicrophoneModule } from './MicrophoneModule';
import { settingsService } from '../settingsService';
import { authService } from '../authService';
import { mqttService } from '../mqttService';
import { logger } from '../../utils/logger';
import { publicAssetUrl } from '../../utils/publicAssetUrl';
import {
  isVoiceDownstreamOpus,
  type MqttAudioDownstreamKind,
  getAudioPlaybackRingBufferFloats,
  getAudioPlaybackMinInterleavedSamples,
  isAudioPlaybackAggressiveRingStart,
} from '../../constants/Codec2Constants';
import type { MqttVoiceUplinkRoute } from './MqttAudioManager';
import { splitOpusMqttPayload } from './opusWebCodecsDownstreamDecoder';
import { OpusDownstreamSession } from './opusDownstreamDecoder';
import {
  createOpusUpstreamEncoder,
  type OpusUpstreamEncoder,
} from './opusUpstreamEncoder';
import {
  concatOpusSubframesWithU16leLengthPrefix,
  getOpusUplinkBatchFrameCount,
} from './opusUplinkMqttBatch';
import { logUplinkStereoS16TelemetryThrottled } from './uplinkPcmLevelTelemetry';
import {
  getAudioContextConstructorSampleRate,
  isMobileAudioEnvironment,
  UPLINK_AUDIO_SAMPLE_RATE,
} from '../../utils/mobileAudioEnvironment';
import { clampUplinkMicGain } from '../../utils/uplinkMicGain';
import type { Pmr171ParamSyncParams } from '../pmr171ParamSync';
import {
  noiseReductionToParamPatch,
  tryParsePmr171ParamSync0x2eFromStatus,
} from '../pmr171ParamSync';
import {
  isPmr171FactoryCalibrationType,
  parseFactoryCalibrationEvent,
  parseFactoryReadAllDoneEvent,
  parseFactoryReadBandDoneEvent,
  type FactoryReadAllDoneEvent,
  type FactoryReadBandDoneEvent,
  type Pmr171FactoryCalibrationEvent,
} from '../pmr171FactoryCalibrationMqtt';
import {
  isPmr171ChannelType,
  parsePmr171ChannelEvent,
  type Pmr171ChannelEvent,
} from '../pmr171ChannelMqtt';
import {
  type AudioSessionState,
  getForceAudioActivationMs,
  getForceCaptureMinFrameCount,
  getOpusWarmupFrameCount,
  getPipelineFirstCaptureTimeoutMs,
  UPLINK_CAPTURE_FRAME_MS,
} from './audioSessionState';
import { hasMqttFrequencyField, readMqttFrequencyHz } from './deviceStatusMqttReceive';

/** device/{userId}/{deviceId}/status 消息 type 归一化（如 0X2D / 0x2d） */
function normalizeDeviceStatusMessageType(t: unknown): string {
  if (t == null) return '';
  return String(t).trim().toUpperCase();
}

/**
 * type=0X2D：双字节表计
 * - firstByte → S/PO 共用半圆表（与 RadioRemotePage signalStrength + signalType 一致）
 * - secondByte → SWR/AUD/ALC 三合一（swrAudAlcType + swrAudAlcValue）
 */
function parseDeviceStatusPayload0x2d(
  data: Record<string, unknown> | null | undefined
): Record<string, unknown> | null {
  if (!data || typeof data !== 'object') return null;
  const out: Record<string, unknown> = {};

  const fb = data.firstByte as { meterKind?: unknown; value?: unknown } | undefined;
  if (fb && typeof fb === 'object') {
    const mk = String(fb.meterKind ?? '').trim().toUpperCase();
    const val = typeof fb.value === 'number' && Number.isFinite(fb.value) ? fb.value : null;
    if (val != null) {
      const tick = Math.min(34, Math.max(0, val));
      out.signalStrength = tick;
      if (mk === 'PO') {
        out.signalType = { name: 'PO_METER' };
      } else {
        out.signalType = { name: 'S_METER' };
      }
    }
  }

  const sb = data.secondByte as { meterKind?: unknown; value?: unknown } | undefined;
  if (sb && typeof sb === 'object') {
    const mk = String(sb.meterKind ?? '').trim().toUpperCase();
    const val = typeof sb.value === 'number' && Number.isFinite(sb.value) ? sb.value : null;
    if (val != null) {
      if (mk === 'SWR') {
        out.swr = val;
        out.swrAudAlcType = { name: 'SWR' };
        out.swrAudAlcValue = val;
      } else if (mk === 'AUD') {
        out.aud = val;
        out.swrAudAlcType = { name: 'AUD' };
        out.swrAudAlcValue = val;
      } else if (mk === 'ALC') {
        out.alc = val;
        out.swrAudAlcType = { name: 'ALC' };
        out.swrAudAlcValue = val;
      }
    }
  }

  return Object.keys(out).length > 0 ? out : null;
}

export interface AudioSystemState {
  isInitialized: boolean;
  microphonePermission: 'granted' | 'denied' | 'prompt';
  activeDeviceId: string | null;
  isConnected: boolean;
  isRecording: boolean;
  isListening: boolean;

  devices: {
    [deviceId: string]: {
      isActive: boolean;
      isPlaying: boolean;
      isRecording: boolean;
      connectionStatus: 'connected' | 'disconnected' | 'connecting';
      audioLevel?: number;
    }
  };
}

export interface AudioManagerEvents {
  'initialized': () => void;
  'connected': () => void;
  'disconnected': () => void;
  'activeDeviceChanged': (data: { deviceId: string; previousDeviceId?: string }) => void;
  'deviceStatusChanged': (data: { deviceId: string; status: any }) => void;
  'deviceFrequencyStatusUpdate': (parsedStatus: any) => void;
  'activeDeviceFrequencyStatusUpdate': (parsedStatus: any) => void;
  'pmr171ParamSync0x2e': (payload: {
    deviceId: string;
    timestamp: number;
    params: Partial<Pmr171ParamSyncParams>;
  }) => void;
  'pmr171FactoryCalibration': (payload: Pmr171FactoryCalibrationEvent) => void;
  'pmr171Channel': (payload: Pmr171ChannelEvent) => void;
  'factoryReadAllDone': (payload: FactoryReadAllDoneEvent) => void;
  'factoryReadBandDone': (payload: FactoryReadBandDoneEvent) => void;
  'microphonePermissionChanged': (permission: 'granted' | 'denied' | 'prompt') => void;
  'connectionChange': (connected: boolean) => void;
  'audioData': (audioData: ArrayBuffer) => void;
  'audioLevel': (level: number) => void;
  'bufferStatus': (status: any) => void;
  'recordingStarted': () => void;
  'recordingStopped': () => void;
  'listeningStarted': (deviceId: string) => void;
  'listeningStopped': () => void;
  'pipelineWarmingStarted': () => void;
  'audioPipelineReady': () => void;
  'error': (error: Error) => void;
}

export interface DecodedDevicePcmFrame {
  deviceId: string;
  samples: Float32Array;
  sampleRate: 48000;
  channels: 2;
  timestamp: number;
}

export type DecodedDevicePcmListener = (frame: DecodedDevicePcmFrame) => void;

export class AudioManager {
  /** 用户组下行 Opus 解码 session 前缀（与 171 deviceId 区分，便于批量清理） */
  static readonly VOICE_GROUP_SESSION_PREFIX = 'voice-group:';

  /** 用户组上行 Opus encoder 作用域（与 deviceId 区分，避免混用 MQTT 主题） */
  static voiceGroupEncoderScopeKey(groupNo: string): string {
    return `${AudioManager.VOICE_GROUP_SESSION_PREFIX}uplink:${groupNo}`;
  }
  private static instance: AudioManager;
  private audioContext: AudioContext | null = null;
  private microphoneModule: MicrophoneModule | null = null;
  
  // AudioWorklet相关
  private audioWorkletNode: AudioWorkletNode | null = null;
  private isWorkletSupported = false;
  private workletInitialized = false;
  
  // 音频缓冲队列
  private audioBufferQueue: Map<string, ArrayBuffer[]> = new Map();
  // 自适应缓冲策略
  private readonly MIN_AUDIO_DURATION_MS = 80; // 最小音频时长80ms，减少延迟
  private readonly MAX_BUFFER_DURATION_MS = 150; // 最大缓冲时长150ms，平衡延迟和流畅度
  private readonly TARGET_BUFFER_DURATION_MS = 120; // 目标缓冲时长120ms
  // 网络状况监控
  private networkLatency: Map<string, number> = new Map();
  private packetLossRate: Map<string, number> = new Map();
  private lastPacketTime: Map<string, number> = new Map();
  
  // 音频播放状态
  private isPlaying: Map<string, boolean> = new Map();
  private lastPlayTime: Map<string, number> = new Map();
  
  // 用户音频管理
  // TODO: 重构为MQTT架构
  // private userAudioManager: UserAudioManager | null = null;
  private activeDeviceId: string | null = null;
  private userId: string = '1'; // 默认用户ID，将在初始化时更新
  private deviceStatusMqttUnsub: (() => void) | null = null;
  
  // 连续播放相关 - 使用AudioWorklet实现连续播放
  private continuousPlaybackNode: AudioWorkletNode | null = null;
  private isInitializingAudioWorklet = false;
  private audioWorkletInitPromise: Promise<void> | null = null;

  /** Opus 下行（…/downstream/opus）：每设备一条 WebCodecs/WASM 解码时间线 */
  private opusDownstreamSessions = new Map<string, OpusDownstreamSession>();
  /** FT8 等独立消费者按设备接收解码后 PCM；不改变 activeDeviceId，也不触发扬声器播放。 */
  private decodedDevicePcmListeners = new Map<string, Set<DecodedDevicePcmListener>>();

  /** Worklet 环缓当前交错样本数（由 status 回传同步） */
  private continuousPlaybackAvailableSamples = 0;

  private continuousPlaybackStatusWaiters: Array<(samples: number) => void> = [];

  /** Opus 上行（优先 WebCodecs，降级 WASM）；与活跃设备对齐，切设备时重置 */
  private opusUplinkEncoder: OpusUpstreamEncoder | null = null;
  private opusUplinkEncoderDeviceId: string | null = null;
  /** 裸 Opus 子帧缓冲，凑满 {@link getOpusUplinkBatchFrameCount} 再发一条 MQTT */
  private opusUplinkPendingRawPackets: ArrayBuffer[] = [];

  /** 缓存动态导入的 MqttAudioManager 模块，避免在热路径中反复 await import() */
  private _mqttAudioManagerMod: { getMqttAudioManager: () => import('./MqttAudioManager').MqttAudioManager | null } | null = null;

  /** V2 会话状态机 */
  private audioSessionState: AudioSessionState = 'INIT';
  /** PTT 上行 MQTT 发布门控（与采集泵解耦） */
  private uplinkPublishGate = false;
  /** 用户组上行 MQTT 发布门控（与 171 PTT gate 独立，共享采集泵 + Opus encoder） */
  private voiceGroupUplinkGate = false;
  private voiceGroupUplinkTopic: string | null = null;
  private voiceGroupEncoderScopeKey: string | null = null;
  private voiceGroupOpusUplinkPendingRawPackets: ArrayBuffer[] = [];
  /** 避免 0X0B 高频推送在 NR/NB 未变化时反复打 paramSync */
  private lastNoiseReductionPatchByDevice = new Map<string, string>();
  /** 采集泵在 PIPELINE_WARMING 成功后保持接通 */
  private capturePumpPermanent = false;
  private pipelineWarmingPromise: Promise<void> | null = null;
  private receivedFirstCaptureFrame = false;
  /** FORCE 层：采集 Worklet 在预激活阶段收到的包数 */
  private forceActivationCaptureCount = 0;
  private forceActivationCaptureTarget = 0;
  private resolveForceCaptureSteady: (() => void) | null = null;
  private rejectForceCaptureSteady: ((err: Error) => void) | null = null;
  private graphForceActivated = false;
  private graphForceActivatedAt = 0;
  private playbackPumpPrimed = false;
  /** Opus 预热阶段丢弃 encoder 输出 */
  private opusWarmupDiscarding = false;
  /** 20ms @ 48kHz stereo s16le */
  private readonly uplinkSilenceFrameBytes = 960 * 2 * 2;

  // 状态管理
  private state: AudioSystemState = {
    isInitialized: false,
    microphonePermission: 'prompt',
    activeDeviceId: null,
    isConnected: false,
    isRecording: false,
    isListening: false,

    devices: {}
  };
  
  // 事件管理
  private eventListeners: Map<keyof AudioManagerEvents, Function[]> = new Map();
  
  // 音频数据包计数
  private audioPacketCount = 0;
  
  // 音频数据接收监控
  private audioDataReceivedTimeout: NodeJS.Timeout | null = null;
  private readonly AUDIO_DATA_TIMEOUT_MS = 10000; // 10秒无音频数据则警告

  /**
   * 辅助录音管线借用计数（如用户组 PTT）：
   * 与设备 startRecording/stopRecording 路径**互斥**，但允许多个非设备消费者同时借用。
   * 第一个借用者负责把 MicrophoneModule.source → AudioWorkletNode 接通，
   * 最后一个释放时再断开并重连回 analyser，避免与设备录音重复连接。
   */
  private _auxMicPumpConsumerCount = 0;

  private constructor() {
    // 音频管理器实例创建
  }
  
  /**
   * 获取单例实例
   */
  static getInstance(): AudioManager {
    if (!AudioManager.instance) {
      AudioManager.instance = new AudioManager();
    }
    return AudioManager.instance;
  }
  
  /**
   * 初始化音频管理器
   */
  async initialize(): Promise<void> {
    try {
      // 简单检查：如果已经初始化且关键组件都存在，则跳过
      if (this.state.isInitialized && this.audioContext && this.microphoneModule) {
        console.log('AudioManager: 音频系统已经完整初始化，跳过重复初始化');
        return;
      }
      
      // 如果状态不一致，重置并重新初始化
      if (this.state.isInitialized) {
        console.log('AudioManager: 检测到状态不一致，重置并重新初始化...');
        await this.dispose();
      }
      
      console.log('AudioManager: 初始化音频系统...');
      
      // 获取当前登录用户ID
      const currentUser = authService.getUser();
      if (currentUser) {
        this.userId = currentUser.id.toString();
        // 使用当前登录用户ID: ${this.userId}
      } else {
        console.warn('AudioManager: 未找到登录用户，使用默认用户ID:', this.userId);
      }
      
      // 修复音频缓冲区大小设置
      settingsService.fixAudioBufferSize();
      
      // 检查浏览器支持
      if (!this.checkBrowserSupport()) {
        throw new Error('浏览器不支持所需的音频API');
      }
      
      // 创建全局音频上下文
      await this.createAudioContext();
      
      // 验证AudioContext是否有效
      if (!this.audioContext) {
        throw new Error('AudioContext创建失败，无法初始化音频系统');
      }
      
      // 创建麦克风模块
      this.microphoneModule = new MicrophoneModule(this.audioContext);
      
      // 设置MicrophoneModule事件监听
      this.setupMicrophoneModuleEvents();
      
      // 初始化MicrophoneModule的AudioWorklet（全局唯一加载 audio-processor.js）
      if (this.isWorkletSupported) {
        await this.microphoneModule.initializeAudioWorklet();
        this.syncWorkletStateFromMicrophoneModule();
      }
      this.logAudioProcessingMode();
      
      // 创建用户音频管理器
      // TODO: 重构为MQTT架构
      // this.userAudioManager = new UserAudioManager(this.userId);
      // this.setupUserAudioManagerEvents();
      
      // 标记为已初始化
      this.state.isInitialized = true;
      
      console.log('AudioManager: 初始化完成');
      this.emit('initialized');
      
    } catch (error) {
      console.error('AudioManager: 音频系统初始化失败:', error);
      this.emit('error', error as Error);
      throw error;
    }
  }
  
  /**
   * 设置MicrophoneModule事件监听
   */
  private setupMicrophoneModuleEvents(): void {
    if (!this.microphoneModule) return;
    
    // console.log('🎤 [测试] 设置MicrophoneModule事件监听器');
    
    // 监听音频数据事件
    this.microphoneModule.on('audioData', (audioData: ArrayBuffer) => {
      if (!this.receivedFirstCaptureFrame) {
        this.receivedFirstCaptureFrame = true;
      }

      if (this.forceActivationCaptureTarget > 0) {
        this.forceActivationCaptureCount += 1;
        if (this.forceActivationCaptureCount >= this.forceActivationCaptureTarget) {
          this.resolveForceCaptureSteady?.();
          this.resolveForceCaptureSteady = null;
          this.rejectForceCaptureSteady = null;
        }
      }

      if (this.voiceGroupUplinkGate && this.voiceGroupEncoderScopeKey) {
        void this.sendVoiceGroupAudioToBackend(audioData);
      } else if (this.state.activeDeviceId && this.uplinkPublishGate) {
        void this.sendAudioToBackend(this.state.activeDeviceId, audioData);
      }
    });
    
    // 监听音频电平事件
    this.microphoneModule.on('audioLevel', (level: number) => {
      // 更新音频电平
      if (this.state.activeDeviceId) {
        this.state = {
          ...this.state,
          devices: {
            ...this.state.devices,
            [this.state.activeDeviceId]: {
              ...this.state.devices[this.state.activeDeviceId],
              audioLevel: level
            }
          }
        };
      }
      
      // 触发音频电平事件
      this.emit('audioLevel', level);
    });
    
    // console.log('🎤 [测试] MicrophoneModule事件监听器设置完成');
  }

  /**
   * 设置用户音频管理器事件监听
   */
  private setupUserAudioManagerEvents(): void {
    // TODO: 重构为MQTT架构
    // setupUserAudioManagerEvents - 已禁用，等待MQTT重构
    /*
    if (!this.userAudioManager) return;
    
    this.userAudioManager.on('connected', () => {
      this.state.isConnected = true;
      this.emit('connected');
      this.emit('connectionChange', true);
    });
    
    this.userAudioManager.on('error', (error: Error) => {
      this.emit('error', error);
    });
    
    this.userAudioManager.on('audioData', (audioData: ArrayBuffer) => {
      this.audioPacketCount++;
      this.emit('audioData', audioData);
    });
    
    this.userAudioManager.on('recordingStarted', () => {
      this.state.isRecording = true;
      this.emit('recordingStarted');
    });
    
    this.userAudioManager.on('recordingStopped', () => {
      this.state.isRecording = false;
      this.emit('recordingStopped');
    });
    
    this.userAudioManager.on('listeningStarted', (deviceId: string) => {
      this.state.isListening = true;
      this.activeDeviceId = deviceId;
      this.state.activeDeviceId = deviceId;
      this.emit('listeningStarted', deviceId);
      this.emit('activeDeviceChanged', { deviceId });
    });
    
    this.userAudioManager.on('listeningStopped', () => {
      this.state.isListening = false;
      this.activeDeviceId = null;
      this.state.activeDeviceId = null;
      this.emit('listeningStopped');
    });
    
    this.userAudioManager.on('deviceStatusChange', (message: any) => {
      this.emit('deviceStatusChanged', { deviceId: message.deviceId, status: message });
    });
    
    // 监听设备频率状态更新事件
    this.userAudioManager.on('deviceFrequencyStatusUpdate', (parsedStatus: any) => {
      this.handleDeviceFrequencyStatusUpdate(parsedStatus);
    });
    
    this.userAudioManager.on('reconnectFailed', () => {
      this.state.isConnected = false;
      this.emit('disconnected');
      this.emit('connectionChange', false);
    });
    */
  }
  
  /**
   * 检查浏览器支持
   */
  private checkBrowserSupport(): boolean {
    const hasAudioContext = !!(window.AudioContext || (window as any).webkitAudioContext);
    const hasGetUserMedia = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    const hasWebSocket = !!window.WebSocket;
    
    // 检查AudioWorklet支持
    try {
      this.isWorkletSupported = !!(window.AudioContext && 'audioWorklet' in window.AudioContext.prototype);
    } catch (error) {
      console.warn('AudioManager: AudioWorklet支持检查失败:', error);
      this.isWorkletSupported = false;
    }
    
    console.log('浏览器支持检查:', {
      AudioContext: hasAudioContext,
      getUserMedia: hasGetUserMedia,
      WebSocket: hasWebSocket,
      AudioWorklet: this.isWorkletSupported
    });
    
    return hasAudioContext && hasGetUserMedia && hasWebSocket;
  }
  
  /**
   * 创建全局音频上下文
   */
  private async createAudioContext(): Promise<void> {
    try {
      const audioSettings = settingsService.getAudioSettings();
      const AC = window.AudioContext || (window as any).webkitAudioContext;
      const requested = getAudioContextConstructorSampleRate(audioSettings.sampleRate);
      try {
        this.audioContext = new AC({ sampleRate: requested });
      } catch (e) {
        console.warn(
          'AudioManager: AudioContext 无法使用请求采样率 %s，回退默认构造:',
          requested,
          e
        );
        this.audioContext = new AC();
      }

      const actual = this.audioContext.sampleRate;
      if (isMobileAudioEnvironment()) {
        const near48 = Math.abs(actual - UPLINK_AUDIO_SAMPLE_RATE) < 1;
        console.log('AudioManager: 移动端 AudioContext 采样率 — 请求', requested, '实际', actual, near48 ? '(将直接 48k 上行)' : '(Worklet 内重采样至 48k)');
      } else {
        console.log('AudioManager: AudioContext 采样率 — 请求', requested, '实际', actual);
      }

      // 处理Chrome的autoplay政策
      if (this.audioContext.state === 'suspended') {
        // 音频上下文被挂起，等待用户交互后恢复
      }
      
      console.log('AudioManager: 音频上下文创建成功 - 状态:', this.audioContext.state);
      
      // audio-processor 仅在 MicrophoneModule.initializeAudioWorklet 中加载一次，避免重复 addModule
      // 导致部分环境下处理器未注册；此处不再单独创建 AudioWorkletNode。
      
      // 显示当前音频处理模式
      this.logAudioProcessingMode();
      
    } catch (error) {
      console.error('AudioManager: 创建音频上下文失败:', error);
      throw error;
    }
  }
  
  /**
   * 与 MicrophoneModule 共用同一录音 AudioWorklet 节点（避免重复 addModule / 双节点不一致）
   */
  private syncWorkletStateFromMicrophoneModule(): void {
    if (!this.microphoneModule) {
      this.audioWorkletNode = null;
      this.workletInitialized = false;
      return;
    }
    const mic = this.microphoneModule as unknown as {
      workletInitialized: boolean;
      audioWorkletNode: AudioWorkletNode | null;
      isWorkletSupported: boolean;
    };
    if (mic.workletInitialized && mic.audioWorkletNode) {
      this.audioWorkletNode = mic.audioWorkletNode;
      this.workletInitialized = true;
    } else {
      this.audioWorkletNode = null;
      this.workletInitialized = false;
      if (mic.isWorkletSupported === false) {
        this.isWorkletSupported = false;
      }
    }
  }

  /**
   * 在 MicrophoneModule 已就绪时加载录音用 Worklet（重建上下文等场景可复用）
   */
  private async initializeAudioWorklet(): Promise<void> {
    if (!this.audioContext || !this.isWorkletSupported || this.workletInitialized) {
      return;
    }
    if (!this.microphoneModule) {
      return;
    }
    await this.microphoneModule.initializeAudioWorklet();
    this.syncWorkletStateFromMicrophoneModule();
    if (this.workletInitialized) {
      setTimeout(() => {
        this.testAudioWorklet();
      }, 1000);
    }
  }
  
  /**
   * 处理来自AudioWorklet的消息
   */
  private handleWorkletMessage(data: any): void {
    try {
      switch (data.type) {
        case 'audioData':
          // 处理录音数据
          this.handleRecordingData(
            data.audioData, 
            data.timestamp, 
            data.level, 
            data.sampleCount, 
            data.channels
          );
          break;
          
        case 'audioLevel':
          // 处理音频电平
          this.handleAudioLevel(data.level, data.timestamp);
          break;
          
        case 'testResponse':
          // 处理测试响应
          console.log('AudioManager: ✅ AudioWorklet测试响应:', data.message);
          break;
          
        default:
          console.warn('AudioManager: 未知的Worklet消息类型:', data.type);
      }
    } catch (error) {
      console.error('AudioManager: 处理Worklet消息失败:', error);
    }
  }
  
  /**
   * 处理录音数据
   */
  private handleRecordingData(audioData: ArrayBuffer, timestamp: number, level: number, sampleCount?: number, channels?: number): void {
    try {
      // 验证音频数据完整性
      if (!audioData || audioData.byteLength === 0) {
        console.warn('AudioManager: 收到空的音频数据，跳过处理');
        return;
      }
      
      // 记录音频数据统计
      if (sampleCount && channels) {
        const expectedSize = sampleCount * channels * 2; // 2 bytes per sample
        if (audioData.byteLength !== expectedSize) {
          console.warn(`AudioManager: 音频数据大小不匹配 - 期望: ${expectedSize}, 实际: ${audioData.byteLength}`);
        }
      }
      
      if (this.state.activeDeviceId && this.state.isRecording) {
        this.sendAudioToBackend(this.state.activeDeviceId, audioData);
      }
      
      // 触发音频数据事件
      this.emit('audioData', audioData);
      
    } catch (error) {
      console.error('AudioManager: 处理录音数据失败:', error);
    }
  }
  
  /**
   * 处理音频电平
   */
  private handleAudioLevel(level: number, timestamp: number): void {
    try {
      // 更新状态中的音频电平
      if (this.state.activeDeviceId) {
        this.state = {
          ...this.state,
          devices: {
            ...this.state.devices,
            [this.state.activeDeviceId]: {
              ...this.state.devices[this.state.activeDeviceId],
              audioLevel: level
            }
          }
        };
      }
      
      // 触发音频电平事件
      this.emit('audioLevel', level);
      
    } catch (error) {
      console.error('AudioManager: 处理音频电平失败:', error);
    }
  }
  
  /**
   * 显示当前音频处理模式
   */
  private logAudioProcessingMode(): void {
    const mode = this.isWorkletSupported && this.workletInitialized ? 'AudioWorklet' : 'ScriptProcessor';
    const performance = mode === 'AudioWorklet' ? '高性能' : '兼容模式';
    
    console.log('🎵 ===== 音频处理模式 =====');
    console.log(`📊 当前模式: ${mode}`);
    console.log(`⚡ 性能等级: ${performance}`);
    console.log(`🔧 支持状态: ${this.isWorkletSupported ? '支持' : '不支持'}`);
    console.log(`✅ 初始化状态: ${this.workletInitialized ? '已初始化' : '未初始化'}`);
    console.log('🎵 ========================');
  }
  
  /**
   * 测试AudioWorklet功能
   */
  async testAudioWorklet(): Promise<boolean> {
    if (!this.audioWorkletNode) {
      console.warn('AudioManager: AudioWorklet节点未初始化，无法测试');
      return false;
    }
    
    try {
      // 发送测试消息到AudioWorklet
      this.audioWorkletNode.port.postMessage({
        type: 'test',
        timestamp: Date.now()
      });
      
      console.log('AudioManager: ✅ AudioWorklet测试消息已发送');
      return true;
    } catch (error) {
      console.error('AudioManager: ❌ AudioWorklet测试失败:', error);
      return false;
    }
  }
  
  /**
   * 更新PTT状态到AudioWorklet
   */
  updatePttState(isActive: boolean): void {
    this.syncWorkletStateFromMicrophoneModule();
    if (!this.audioWorkletNode) {
      console.warn('AudioManager: AudioWorklet节点未初始化，无法更新PTT状态');
      return;
    }
    
    try {
      this.audioWorkletNode.port.postMessage({
        type: 'pttState',
        data: { isActive },
        timestamp: Date.now()
      });
      
      console.log(`AudioManager: PTT状态已更新到AudioWorklet - 激活: ${isActive}`);
    } catch (error) {
      console.error('AudioManager: 更新PTT状态到AudioWorklet失败:', error);
    }
  }

  getAudioSessionState(): AudioSessionState {
    return this.audioSessionState;
  }

  /** V2：采集+编码管线已就绪，允许 PTT（须完成 FORCE 静音预激活） */
  isAudioReady(): boolean {
    if (!this.graphForceActivated) {
      return false;
    }
    return (
      this.audioSessionState === 'AUDIO_READY' ||
      this.audioSessionState === 'IDLE' ||
      this.audioSessionState === 'PTT_ACTIVE' ||
      this.audioSessionState === 'STREAMING'
    );
  }

  /**
   * PTT 上行 MQTT 发布门控（不负责初始化 AudioGraph）
   */
  setUplinkPublishGate(open: boolean): void {
    this.uplinkPublishGate = open;
    const recId = this.state.activeDeviceId;
    if (open) {
      this.audioSessionState = 'PTT_ACTIVE';
    } else if (this.capturePumpPermanent) {
      this.audioSessionState = 'AUDIO_READY';
    }
    if (recId) {
      this.state = {
        ...this.state,
        isRecording: open,
        devices: {
          ...this.state.devices,
          [recId]: {
            ...this.state.devices[recId],
            isRecording: open,
          },
        },
      };
    } else {
      this.state = { ...this.state, isRecording: open };
    }
    if (open) {
      this.emit('recordingStarted');
    } else {
      this.emit('recordingStopped');
    }
  }

  /**
   * V2 PIPELINE_WARMING：主动预热 AudioGraph + Opus，与 downlink/PTT 解耦
   * @param encoderScopeKey 非设备场景（如用户组）的 Opus encoder 作用域，与 deviceId 二选一
   */
  async beginPipelineWarming(deviceId?: string, encoderScopeKey?: string): Promise<void> {
    if (this.isAudioReady()) {
      if (encoderScopeKey) {
        await this.ensureOpusUplinkEncoder(encoderScopeKey);
      }
      if (deviceId) {
        await this.ensureDeviceSubscribed(deviceId);
      }
      return;
    }
    if (this.pipelineWarmingPromise) {
      await this.pipelineWarmingPromise;
      if (encoderScopeKey) {
        await this.ensureOpusUplinkEncoder(encoderScopeKey);
      }
      if (deviceId) {
        await this.ensureDeviceSubscribed(deviceId);
      }
      return;
    }
    this.pipelineWarmingPromise = this.runPipelineWarming(deviceId, encoderScopeKey);
    try {
      await this.pipelineWarmingPromise;
    } finally {
      this.pipelineWarmingPromise = null;
    }
  }

  private async runPipelineWarming(deviceId?: string, encoderScopeKey?: string): Promise<void> {
    this.audioSessionState = 'PIPELINE_WARMING';
    this.emit('pipelineWarmingStarted');
    this.receivedFirstCaptureFrame = false;
    this.forceActivationCaptureCount = 0;
    this.forceActivationCaptureTarget = 0;
    this.resolveForceCaptureSteady = null;
    this.rejectForceCaptureSteady = null;

    if (!this.state.isInitialized || !this.microphoneModule) {
      await this.initialize();
    }
    if (!this.state.isConnected) {
      await this.connect();
    }

    this.kickAudioContextOnUserGesture();
    await this.resumeAudioContext();

    if (this.microphoneModule && !this.microphoneModule.hasPermission()) {
      await this.microphoneModule.requestPermission();
      this.state.microphonePermission = 'granted';
    }

    if (!this.workletInitialized && this.microphoneModule) {
      await this.microphoneModule.initializeAudioWorklet();
      this.syncWorkletStateFromMicrophoneModule();
    }

    if (this.microphoneModule) {
      await this.microphoneModule.ensureCapturePumpConnected();
    }
    this.capturePumpPermanent = true;

    const encoderDeviceId = String(encoderScopeKey ?? deviceId ?? this.activeDeviceId ?? '0');

    let encOk: boolean;
    if (!this.shouldSkipForceActivation()) {
      const [, encoderReady] = await Promise.all([
        this.forceAudioGraphActivation(),
        this.ensureOpusUplinkEncoder(encoderDeviceId),
      ]);
      encOk = encoderReady;
    } else {
      console.info('[WaveLink FORCE_AUDIO] 跳过重复预激活（graph 仍 warm）');
      encOk = await this.ensureOpusUplinkEncoder(encoderDeviceId);
    }

    if (!encOk) {
      this.audioSessionState = 'MQTT_CONNECTED';
      this.graphForceActivated = false;
      throw new Error('Opus 编码器不可用（WebCodecs 与 WASM 降级均初始化失败）');
    }

    await this.warmUpOpusUplinkEncoder();

    this.graphForceActivated = true;
    this.graphForceActivatedAt = Date.now();
    this.audioSessionState = 'AUDIO_READY';
    console.info('[WaveLink AudioSession] AUDIO_READY', {
      captureFramesDuringForce: this.forceActivationCaptureCount,
      playbackPumpPrimed: this.playbackPumpPrimed,
      audioContextState: this.audioContext?.state,
    });
    this.emit('audioPipelineReady');

    if (deviceId) {
      await this.ensureDeviceSubscribed(deviceId);
    }
  }

  private shouldSkipForceActivation(): boolean {
    if (!this.graphForceActivated || !this.playbackPumpPrimed) {
      return false;
    }
    if (this.audioContext?.state !== 'running') {
      return false;
    }
    if (!this.microphoneModule?.isCapturePumpConnected()) {
      return false;
    }
    const age = Date.now() - this.graphForceActivatedAt;
    return age < 60_000;
  }

  /**
   * FORCE_AUDIO_GRAPH_ACTIVATION：静音预激活采集+播放图，与 downlink 解耦。
   * 采集侧：等 Worklet 连续 N 包（≈1～2s）；播放侧：向 playback Worklet 喂静音 PCM。
   */
  private async forceAudioGraphActivation(): Promise<void> {
    if (!this.audioContext) {
      throw new Error('AudioContext 未初始化');
    }

    await this.resumeAudioContext();

    if (this.microphoneModule) {
      await this.microphoneModule.ensureCapturePumpConnected();
    }

    const durationMs = getForceAudioActivationMs();
    const minFrames = getForceCaptureMinFrameCount();
    this.forceActivationCaptureCount = 0;
    this.forceActivationCaptureTarget = minFrames;

    const captureSteadyPromise = this.waitForForceCaptureSteady(
      minFrames,
      durationMs + getPipelineFirstCaptureTimeoutMs()
    );

    const playbackSilencePromise = this.injectPlaybackSilencePcm(durationMs);

    console.info('[WaveLink FORCE_AUDIO] 开始预激活', {
      durationMs,
      minCaptureFrames: minFrames,
      frameMs: UPLINK_CAPTURE_FRAME_MS,
    });

    try {
      await Promise.all([captureSteadyPromise, playbackSilencePromise]);
    } finally {
      this.forceActivationCaptureTarget = 0;
    }

    if (this.audioContext.state !== 'running') {
      await this.resumeAudioContext();
    }

    this.playbackPumpPrimed = true;
    console.info('[WaveLink FORCE_AUDIO] 预激活完成', {
      captureFrames: this.forceActivationCaptureCount,
      audioContextState: this.audioContext.state,
    });
  }

  private waitForForceCaptureSteady(minFrames: number, timeoutMs: number): Promise<void> {
    if (this.forceActivationCaptureCount >= minFrames) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve, reject) => {
      this.resolveForceCaptureSteady = () => {
        if (this.forceActivationCaptureCount >= minFrames) {
          resolve();
        }
      };
      this.rejectForceCaptureSteady = reject;
      window.setTimeout(() => {
        if (this.forceActivationCaptureCount >= minFrames) {
          resolve();
          return;
        }
        this.resolveForceCaptureSteady = null;
        this.rejectForceCaptureSteady = null;
        reject(
          new Error(
            `FORCE 采集激活不足：${this.forceActivationCaptureCount}/${minFrames} 包（Worklet 未 steady）`
          )
        );
      }, timeoutMs);
    });
  }

  /**
   * 播放侧：向连续播放 Worklet 注入静音 PCM，维持 destination 拉流 ≈1～2s。
   */
  private async injectPlaybackSilencePcm(durationMs: number): Promise<void> {
    const silence = new ArrayBuffer(this.uplinkSilenceFrameBytes);
    const endAt = Date.now() + durationMs;
    let packets = 0;

    while (Date.now() < endAt) {
      if (this.audioContext?.state === 'suspended') {
        await this.resumeAudioContext();
      }
      await this.playAudioContinuously(silence);
      packets += 1;
      await new Promise<void>((r) => window.setTimeout(r, UPLINK_CAPTURE_FRAME_MS));
    }

    console.info('[WaveLink FORCE_AUDIO] 播放侧静音注入完成', { packets, durationMs });
  }

  private async ensureOpusUplinkEncoder(deviceId: string): Promise<boolean> {
    const encDeviceId = String(deviceId);
    if (
      this.opusUplinkEncoder &&
      this.opusUplinkEncoderDeviceId === encDeviceId &&
      this.opusUplinkEncoder.isConfigured()
    ) {
      return true;
    }
    if (this.opusUplinkEncoder) {
      this.resetOpusUplinkEncoder();
    }
    const enc = await createOpusUpstreamEncoder((buf) => {
      if (this.opusWarmupDiscarding) {
        return;
      }
      if (
        this.voiceGroupUplinkGate &&
        this.voiceGroupEncoderScopeKey === encDeviceId
      ) {
        this.enqueueVoiceGroupOpusUplinkRawPacket(buf);
        return;
      }
      if (!this.uplinkPublishGate) {
        return;
      }
      if (String(this.state.activeDeviceId) !== encDeviceId) {
        return;
      }
      this.enqueueOpusUplinkRawPacketForBatchPublish(encDeviceId, buf);
    });
    if (!enc) {
      return false;
    }
    this.opusUplinkEncoder = enc;
    this.opusUplinkEncoderDeviceId = encDeviceId;
    return true;
  }

  private async warmUpOpusUplinkEncoder(): Promise<void> {
    if (!this.opusUplinkEncoder) {
      return;
    }
    this.opusWarmupDiscarding = true;
    const silence = new ArrayBuffer(this.uplinkSilenceFrameBytes);
    const frames = getOpusWarmupFrameCount();
    for (let i = 0; i < frames; i++) {
      this.opusUplinkEncoder.feedStereoS16le48k(silence);
    }
    try {
      await this.opusUplinkEncoder.flush();
      await new Promise<void>((r) => queueMicrotask(r));
      await new Promise<void>((r) => queueMicrotask(r));
    } catch (e) {
      console.warn('[WaveLink Opus uplink] warmUp flush 失败', e);
    }
    this.opusWarmupDiscarding = false;
    console.info(`[WaveLink Opus uplink] warmUp 完成 (${frames} 帧静音)`);
  }

  /** PTT 半双工：清空下行播放缓冲，不取消 MQTT 订阅 */
  pauseDownstreamPlaybackForPtt(): void {
    if (this.continuousPlaybackNode) {
      try {
        this.continuousPlaybackNode.port.postMessage({ type: 'clear' });
      } catch (e) {
        console.warn('AudioManager: 清空播放环缓失败', e);
      }
    }
  }

  private resetOpusUplinkEncoder(): void {
    this.opusUplinkEncoder?.close();
    this.opusUplinkEncoder = null;
    this.opusUplinkEncoderDeviceId = null;
    this.opusUplinkPendingRawPackets = [];
  }

  /**
   * 将编码器输出的裸 Opus 包攒批：`u16le+opus` 多段拼一包 MQTT，减轻设备侧压力。
   */
  private enqueueOpusUplinkRawPacketForBatchPublish(deviceId: string, rawOpus: ArrayBuffer): void {
    if (!this.uplinkPublishGate) {
      return;
    }
    if (String(this.state.activeDeviceId) !== String(deviceId)) {
      return;
    }
    this.opusUplinkPendingRawPackets.push(rawOpus.slice(0));
    const batch = getOpusUplinkBatchFrameCount();
    while (this.opusUplinkPendingRawPackets.length >= batch) {
      const slice = this.opusUplinkPendingRawPackets.splice(0, batch);
      const merged = concatOpusSubframesWithU16leLengthPrefix(slice);
      if (merged.byteLength > 0) {
        void this.publishUplinkAudioToMqtt(deviceId, merged, 'user_opus');
      }
    }
  }

  /** PTT 结束或切设备前：发出不足一批的尾帧 */
  private flushOpusUplinkPendingBatch(deviceId: string): void {
    if (this.opusUplinkPendingRawPackets.length === 0) {
      return;
    }
    const merged = concatOpusSubframesWithU16leLengthPrefix(this.opusUplinkPendingRawPackets);
    this.opusUplinkPendingRawPackets = [];
    if (merged.byteLength > 0) {
      void this.publishUplinkAudioToMqtt(deviceId, merged, 'user_opus');
    }
  }

  private enqueueVoiceGroupOpusUplinkRawPacket(rawOpus: ArrayBuffer): void {
    if (!this.voiceGroupUplinkGate || !this.voiceGroupUplinkTopic) {
      return;
    }
    this.voiceGroupOpusUplinkPendingRawPackets.push(rawOpus.slice(0));
    const batch = getOpusUplinkBatchFrameCount();
    while (this.voiceGroupOpusUplinkPendingRawPackets.length >= batch) {
      const slice = this.voiceGroupOpusUplinkPendingRawPackets.splice(0, batch);
      const merged = concatOpusSubframesWithU16leLengthPrefix(slice);
      if (merged.byteLength > 0) {
        void this.publishVoiceGroupUplinkToMqtt(this.voiceGroupUplinkTopic, merged);
      }
    }
  }

  private flushVoiceGroupOpusUplinkPendingBatch(): void {
    const topic = this.voiceGroupUplinkTopic;
    if (!topic || this.voiceGroupOpusUplinkPendingRawPackets.length === 0) {
      this.voiceGroupOpusUplinkPendingRawPackets = [];
      return;
    }
    const merged = concatOpusSubframesWithU16leLengthPrefix(this.voiceGroupOpusUplinkPendingRawPackets);
    this.voiceGroupOpusUplinkPendingRawPackets = [];
    if (merged.byteLength > 0) {
      void this.publishVoiceGroupUplinkToMqtt(topic, merged);
    }
  }

  private async flushVoiceGroupOpusUplinkTail(): Promise<void> {
    if (!this.opusUplinkEncoder) {
      this.flushVoiceGroupOpusUplinkPendingBatch();
      return;
    }
    try {
      await this.opusUplinkEncoder.flush();
      await new Promise<void>((r) => queueMicrotask(r));
      await new Promise<void>((r) => queueMicrotask(r));
    } catch (e) {
      console.warn('AudioManager: 用户组 Opus 上行 flush 失败', e);
    }
    this.flushVoiceGroupOpusUplinkPendingBatch();
  }

  private async publishVoiceGroupUplinkToMqtt(topic: string, payload: ArrayBuffer): Promise<void> {
    try {
      const mqttManager = mqttService.getMqttManager();
      if (!mqttManager) {
        console.warn('AudioManager: MQTT 未就绪，无法发送用户组上行');
        return;
      }
      if (payload.byteLength === 0) {
        return;
      }
      const buffer = typeof Buffer !== 'undefined'
        ? Buffer.from(payload)
        : (new Uint8Array(payload) as unknown as Buffer);
      await mqttManager.publish(topic, buffer, 0, false);
    } catch (error) {
      console.error('AudioManager: 发送用户组上行音频失败:', error);
    }
  }

  /** PTT 松开：flush 尾包，保留 encoder 与采集泵 */
  private async flushOpusUplinkTailForPtt(): Promise<void> {
    const deviceId = this.state.activeDeviceId ? String(this.state.activeDeviceId) : null;
    if (!this.opusUplinkEncoder) {
      if (deviceId) {
        this.flushOpusUplinkPendingBatch(deviceId);
      } else {
        this.opusUplinkPendingRawPackets = [];
      }
      return;
    }
    try {
      await this.opusUplinkEncoder.flush();
      await new Promise<void>((r) => queueMicrotask(r));
      await new Promise<void>((r) => queueMicrotask(r));
    } catch (e) {
      console.warn('AudioManager: Opus上行 flush 失败', e);
    }
    if (deviceId) {
      this.flushOpusUplinkPendingBatch(deviceId);
    } else {
      this.opusUplinkPendingRawPackets = [];
    }
  }

  private async flushOpusUplinkEncoder(): Promise<void> {
    const deviceId = this.state.activeDeviceId ? String(this.state.activeDeviceId) : null;
    if (!this.opusUplinkEncoder) {
      if (deviceId) {
        this.flushOpusUplinkPendingBatch(deviceId);
      } else {
        this.opusUplinkPendingRawPackets = [];
      }
      return;
    }
    try {
      await this.opusUplinkEncoder.flush();
      // 部分环境 encode 输出在 flush resolve 后仍晚一拍投递，微任务后再拼尾包
      await new Promise<void>((r) => queueMicrotask(r));
      await new Promise<void>((r) => queueMicrotask(r));
    } catch (e) {
      console.warn('AudioManager: Opus上行 flush 失败', e);
    }
    if (deviceId) {
      this.flushOpusUplinkPendingBatch(deviceId);
    } else {
      this.opusUplinkPendingRawPackets = [];
    }
    this.opusUplinkEncoder?.close();
    this.opusUplinkEncoder = null;
    this.opusUplinkEncoderDeviceId = null;
  }

  /**
   * 发送音频数据到后端（上行固定 Opus / upstream/opus）
   */
  private async sendAudioToBackend(deviceId: string, audioData: ArrayBuffer): Promise<void> {
    try {
      if (!this.uplinkPublishGate) {
        return;
      }

      const nowPerf = () =>
        (typeof performance !== 'undefined' && typeof performance.now === 'function'
          ? performance.now()
          : Date.now());
      const g = globalThis as any;
      const perf = g.__WL_PTT_PERF__;
      if (perf && !perf.firstFrameLogged) {
        const now = nowPerf();
        perf.firstFrameLogged = true;
        const tClickToFirstFrame = now - (perf.clickTs || now);
        const tPttOnToFirstFrame = perf.pttOnAckTs ? now - perf.pttOnAckTs : NaN;
        const tStartRecordingToFirstFrame = perf.startRecordingTs ? now - perf.startRecordingTs : NaN;
        console.warn(
          `[PTT PERF] trace=${perf.traceId || 'unknown'} first-audio-frame ` +
            `t_click_to_firstFrame_ms=${tClickToFirstFrame.toFixed(1)} ` +
            `t_pttOn_to_firstFrame_ms=${Number.isFinite(tPttOnToFirstFrame) ? tPttOnToFirstFrame.toFixed(1) : 'n/a'} ` +
            `t_startRecording_to_firstFrame_ms=${Number.isFinite(tStartRecordingToFirstFrame) ? tStartRecordingToFirstFrame.toFixed(1) : 'n/a'}`
        );
        g.__WL_PTT_PERF__ = null;
      }

      const encOk = await this.ensureOpusUplinkEncoder(String(deviceId));
      if (!encOk || !this.opusUplinkEncoder) {
        console.warn('[WaveLink Opus uplink] 编码器未就绪，跳过本帧');
        return;
      }
      logUplinkStereoS16TelemetryThrottled(String(deviceId), 'opus_pre_encode', audioData);
      this.opusUplinkEncoder.feedStereoS16le48k(audioData);
    } catch (error) {
      console.error('AudioManager: 发送音频数据到后端失败:', error);
    }
  }

  /** 用户组上行：共享采集泵 + Opus encoder，MQTT 主题独立于 171 PTT */
  private async sendVoiceGroupAudioToBackend(audioData: ArrayBuffer): Promise<void> {
    try {
      if (!this.voiceGroupUplinkGate || !this.voiceGroupEncoderScopeKey) {
        return;
      }
      const encOk = await this.ensureOpusUplinkEncoder(this.voiceGroupEncoderScopeKey);
      if (!encOk || !this.opusUplinkEncoder) {
        console.warn('[WaveLink Opus uplink] 用户组编码器未就绪，跳过本帧');
        return;
      }
      this.opusUplinkEncoder.feedStereoS16le48k(audioData);
    } catch (error) {
      console.error('AudioManager: 发送用户组音频失败:', error);
    }
  }

  /** MQTT 发布上行音频：PCM 为 48 kHz 立体声 s16le；Opus 为 WebCodecs 编码裸帧 */
  private async publishUplinkAudioToMqtt(
    deviceId: string,
    payload: ArrayBuffer,
    uplinkRoute: MqttVoiceUplinkRoute
  ): Promise<void> {
    if (!this._mqttAudioManagerMod) {
      this._mqttAudioManagerMod = await import('./MqttAudioManager');
    }
    const mqttAudioManager = this._mqttAudioManagerMod.getMqttAudioManager();
    if (!mqttAudioManager) {
      console.warn('AudioManager: MqttAudioManager 未就绪，无法上行音频');
      return;
    }
    await mqttAudioManager.sendAudioToDevice(deviceId, payload, uplinkRoute);
    if (this.uplinkPublishGate && this.audioSessionState === 'PTT_ACTIVE') {
      this.audioSessionState = 'STREAMING';
    }
  }
  
  /**
   * 连接到MQTT音频系统
   */
  async connect(): Promise<void> {
    try {
      if (!this.state.isInitialized || !this.microphoneModule) {
        throw new Error('音频管理器未初始化，无法连接MQTT音频系统');
      }

      this.deviceStatusMqttUnsub?.();
      this.deviceStatusMqttUnsub = mqttService.addDeviceStatusListener((deviceId: string, status: any) => {
        console.log(`AudioManager: 收到设备状态更新 - 设备ID: ${deviceId}`, status);
        this.emit('deviceStatusChanged', { deviceId, status });

        let statusObj = status;
        if (typeof status === 'string') {
          try {
            statusObj = JSON.parse(status);
          } catch (e) {
            console.warn('AudioManager: 无法解析状态字符串:', status);
            return;
          }
        }

        const frequencyStatus =
          statusObj && (statusObj.data !== undefined && statusObj.data !== null)
            ? statusObj.data
            : statusObj;

        const msgType = normalizeDeviceStatusMessageType(
          statusObj && typeof statusObj === 'object' && statusObj !== null && 'type' in statusObj
            ? (statusObj as { type?: unknown }).type
            : undefined
        );

        if (msgType === 'FACTORY_READ_ALL_DONE' || msgType === 'FACTORY_READ_BAND_DONE') {
          const data =
            frequencyStatus && typeof frequencyStatus === 'object'
              ? (frequencyStatus as Record<string, unknown>)
              : {};
          const timestamp =
            statusObj != null &&
            typeof statusObj === 'object' &&
            typeof (statusObj as { timestamp?: unknown }).timestamp === 'number'
              ? (statusObj as { timestamp: number }).timestamp
              : Date.now();
          if (msgType === 'FACTORY_READ_ALL_DONE') {
            const event = parseFactoryReadAllDoneEvent(deviceId, msgType, data, timestamp);
            if (event) {
              this.emit('factoryReadAllDone', event);
            }
            return;
          }
          const event = parseFactoryReadBandDoneEvent(deviceId, msgType, data, timestamp);
          if (event) {
            this.emit('factoryReadBandDone', event);
          }
          return;
        }

        if (isPmr171ChannelType(msgType)) {
          const data =
            frequencyStatus && typeof frequencyStatus === 'object'
              ? (frequencyStatus as Record<string, unknown>)
              : {};
          const timestamp =
            statusObj != null &&
            typeof statusObj === 'object' &&
            typeof (statusObj as { timestamp?: unknown }).timestamp === 'number'
              ? (statusObj as { timestamp: number }).timestamp
              : Date.now();
          const event = parsePmr171ChannelEvent(deviceId, msgType, data, timestamp);
          if (event) {
            this.emit('pmr171Channel', event);
          }
          return;
        }

        if (isPmr171FactoryCalibrationType(msgType)) {
          const data =
            frequencyStatus && typeof frequencyStatus === 'object'
              ? (frequencyStatus as Record<string, unknown>)
              : {};
          const timestamp =
            statusObj != null &&
            typeof statusObj === 'object' &&
            typeof (statusObj as { timestamp?: unknown }).timestamp === 'number'
              ? (statusObj as { timestamp: number }).timestamp
              : Date.now();
          const event = parseFactoryCalibrationEvent(deviceId, msgType, data, timestamp);
          if (event) {
            this.emit('pmr171FactoryCalibration', event);
          }
          return;
        }

        if (msgType === '0X2E') {
          const parsed = tryParsePmr171ParamSync0x2eFromStatus(statusObj, frequencyStatus, deviceId);
          if (parsed) {
            this.emit('pmr171ParamSync0x2e', parsed);
          }
          return;
        }

        if (msgType === '0X0B') {
          const nrNbSource =
            frequencyStatus && typeof frequencyStatus === 'object'
              ? (frequencyStatus as Record<string, unknown>)
              : {};
          const nrNbPatch = noiseReductionToParamPatch(nrNbSource.noiseReduction);
          if (nrNbPatch) {
            const signature = `${nrNbPatch.nr},${nrNbPatch.nb}`;
            if (this.lastNoiseReductionPatchByDevice.get(deviceId) !== signature) {
              this.lastNoiseReductionPatchByDevice.set(deviceId, signature);
              const timestamp =
                statusObj != null &&
                typeof statusObj === 'object' &&
                typeof (statusObj as { timestamp?: unknown }).timestamp === 'number'
                  ? (statusObj as { timestamp: number }).timestamp
                  : Date.now();
              this.emit('pmr171ParamSync0x2e', {
                deviceId,
                timestamp,
                params: nrNbPatch,
              });
            }
          }
        }

        const payload0x2d =
          msgType === '0X2D'
            ? parseDeviceStatusPayload0x2d(frequencyStatus as Record<string, unknown>)
            : null;

        const hasVfoPayload =
          statusObj &&
          (hasMqttFrequencyField(statusObj, 'vfoAFrequency') ||
            hasMqttFrequencyField(statusObj, 'vfoBFrequency') ||
            (statusObj.data &&
              (hasMqttFrequencyField(statusObj.data, 'vfoAFrequency') ||
                hasMqttFrequencyField(statusObj.data, 'vfoBFrequency'))));

        const hasMeterBarsPayload =
          frequencyStatus &&
          (typeof frequencyStatus.sMeterBars === 'number' ||
            typeof frequencyStatus.poMeterBars === 'number' ||
            typeof frequencyStatus.signalStrength === 'number');

        /** 远程控制台等：设备经 TCP/服务端进入 MQTT 的 SWR、音频、ALC 读数 */
        const hasRadioRemoteMetricsPayload =
          frequencyStatus &&
          (typeof frequencyStatus.swr === 'number' ||
            typeof frequencyStatus.aud === 'number' ||
            typeof frequencyStatus.alc === 'number' ||
            typeof frequencyStatus.swrAudAlcValue === 'number' ||
            frequencyStatus.swrAudAlcType != null);

        const has0x2dMeterPayload = payload0x2d != null;

        if (
          statusObj &&
          (hasVfoPayload ||
            hasMeterBarsPayload ||
            hasRadioRemoteMetricsPayload ||
            has0x2dMeterPayload)
        ) {
          const resolvedDeviceId =
            (statusObj as { deviceId?: string | number }).deviceId ??
            (frequencyStatus as { deviceId?: string | number }).deviceId ??
            deviceId;

          const tsRaw =
            statusObj && typeof statusObj === 'object' && statusObj !== null && 'timestamp' in statusObj
              ? (statusObj as { timestamp?: number }).timestamp
              : undefined;
          const tsData =
            frequencyStatus &&
            typeof frequencyStatus === 'object' &&
            frequencyStatus !== null &&
            'timestamp' in frequencyStatus
              ? (frequencyStatus as { timestamp?: number }).timestamp
              : undefined;

          /** 仅附带 payload 里真实存在的 VFO 字段；否则纯表计/0x2D 包会把频率写成 0、模式写成 Unknown，造成前端与完整状态包交替「闪烁」。 */
          const vfoAHz = readMqttFrequencyHz(frequencyStatus.vfoAFrequency);
          const vfoBHz = readMqttFrequencyHz(frequencyStatus.vfoBFrequency);
          const fullStatus = {
            deviceId: resolvedDeviceId,
            ...(msgType ? { statusType: msgType } : {}),
            ...(vfoAHz !== undefined ? { vfoAFrequency: vfoAHz } : {}),
            ...(vfoBHz !== undefined ? { vfoBFrequency: vfoBHz } : {}),
            ...(typeof frequencyStatus.vfoAMode === 'string' ? { vfoAMode: frequencyStatus.vfoAMode } : {}),
            ...(typeof frequencyStatus.vfoBMode === 'string' ? { vfoBMode: frequencyStatus.vfoBMode } : {}),
            isOnline: frequencyStatus.isOnline !== undefined ? frequencyStatus.isOnline : true,
            deviceIp: frequencyStatus.deviceIp || '',
            timestamp: tsRaw ?? tsData ?? Date.now(),
            ...(typeof frequencyStatus.sMeterBars === 'number'
              ? { sMeterBars: frequencyStatus.sMeterBars }
              : {}),
            ...(typeof frequencyStatus.poMeterBars === 'number'
              ? { poMeterBars: frequencyStatus.poMeterBars }
              : {}),
            ...(typeof frequencyStatus.signalStrength === 'number'
              ? { signalStrength: frequencyStatus.signalStrength }
              : {}),
            ...(frequencyStatus.signalType !== undefined && frequencyStatus.signalType !== null
              ? { signalType: frequencyStatus.signalType }
              : {}),
            ...(typeof frequencyStatus.swr === 'number' ? { swr: frequencyStatus.swr } : {}),
            ...(typeof frequencyStatus.aud === 'number' ? { aud: frequencyStatus.aud } : {}),
            ...(typeof frequencyStatus.alc === 'number' ? { alc: frequencyStatus.alc } : {}),
            ...(typeof frequencyStatus.swrAudAlcValue === 'number'
              ? { swrAudAlcValue: frequencyStatus.swrAudAlcValue }
              : {}),
            ...(frequencyStatus.swrAudAlcType !== undefined && frequencyStatus.swrAudAlcType !== null
              ? { swrAudAlcType: frequencyStatus.swrAudAlcType }
              : {}),
            ...(typeof frequencyStatus.filterBandwidth === 'number'
              ? { filterBandwidth: frequencyStatus.filterBandwidth }
              : {}),
            ...(payload0x2d || {}),
          };

          console.log(`AudioManager: 触发设备频率状态更新 - 设备ID: ${fullStatus.deviceId}`, fullStatus);

          this.emit('deviceFrequencyStatusUpdate', fullStatus);

          if (
            String(this.activeDeviceId) === String(deviceId) ||
            String(this.activeDeviceId) === String(fullStatus.deviceId)
          ) {
            this.emit('activeDeviceFrequencyStatusUpdate', fullStatus);
          }
        }
      });

      // 设置MQTT服务回调（设备状态走 addDeviceStatusListener，避免与 RealtimeService 的 setCallbacks 互相覆盖）
      mqttService.setCallbacks({
        onConnect: () => {
          this.state.isConnected = true;
          this.emit('connected');
        },
        onDisconnect: () => {
          this.state.isConnected = false;
          this.emit('disconnected');
        },
        onAudioReceived: (deviceId: string, audioData: ArrayBuffer, downstreamKind?: MqttAudioDownstreamKind) => {
          this.resetAudioDataReceivedTimeout();

          const activeDeviceIdStr = this.activeDeviceId ? String(this.activeDeviceId) : null;
          const receivedDeviceIdStr = String(deviceId);

          if (
            !this.isAudioReady() &&
            !this.pipelineWarmingPromise &&
            activeDeviceIdStr &&
            activeDeviceIdStr === receivedDeviceIdStr
          ) {
            void this.beginPipelineWarming(receivedDeviceIdStr).catch((err) => {
              console.warn('[WaveLink FORCE_AUDIO] 下行后补预热失败', err);
            });
          }
          
          if (activeDeviceIdStr && activeDeviceIdStr === receivedDeviceIdStr) {
            this.playReceivedAudio(deviceId, audioData, downstreamKind);
          } else if (this.hasDecodedDevicePcmListeners(receivedDeviceIdStr)) {
            const kind: MqttAudioDownstreamKind =
              downstreamKind ?? (isVoiceDownstreamOpus() ? 'opus' : 'pcm');
            if (kind === 'opus') {
              void this.deliverOpusDownstreamInternal(
                receivedDeviceIdStr,
                audioData,
                undefined,
                false,
              );
            } else {
              const samples = this.convertAudioToFloat32Array(audioData);
              if (samples) this.notifyDecodedDevicePcm(receivedDeviceIdStr, samples);
            }
          } else {
            console.warn('[WaveLink voice downstream] 跳过非当前活跃设备的音频', {
              receivedDeviceId: receivedDeviceIdStr,
              activeDeviceId: activeDeviceIdStr,
              byteLength: audioData.byteLength,
            });
          }
        },
        onError: (error: Error) => {
          console.error('AudioManager: MQTT错误:', error);
          this.emit('error', error);
        }
      });
      
      // 连接到MQTT服务（如果未初始化会自动初始化）
      await mqttService.connect();
      
      if (this.audioSessionState === 'INIT') {
        this.audioSessionState = 'MQTT_CONNECTED';
      }
      console.log('AudioManager: MQTT连接成功');
    } catch (error) {
      console.error('AudioManager: MQTT音频系统连接失败:', error);
      throw error;
    }
  }
  
  /**
   * 请求麦克风权限
   */
  async requestMicrophonePermission(): Promise<void> {
    if (!this.state.isInitialized || !this.microphoneModule) {
      throw new Error('音频管理器未初始化');
    }
    
    try {
      await this.microphoneModule.requestPermission();
      this.state.microphonePermission = 'granted';
      this.emit('microphonePermissionChanged', 'granted');
      console.log('AudioManager: 麦克风权限已获取');
    } catch (error) {
      this.state.microphonePermission = 'denied';
      this.emit('microphonePermissionChanged', 'denied');
      console.error('AudioManager: 麦克风权限获取失败:', error);
      throw error;
    }
  }
  
  /**
   * 订阅设备音频（带重试机制）
   */
  private async subscribeToDeviceAudioWithRetry(deviceId: string, maxRetries: number = 3): Promise<boolean> {
    console.log(`AudioManager: [订阅重试] subscribeToDeviceAudioWithRetry 开始 - 设备ID: ${deviceId}, 最大重试次数: ${maxRetries}`);
    
    const { mqttService } = await import('../mqttService');
    console.log(`AudioManager: [订阅重试] mqttService 导入完成`);
    console.log(`AudioManager: [订阅重试] mqttService.isServiceInitialized(): ${mqttService.isServiceInitialized()}`);
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`AudioManager: [订阅重试] 尝试订阅设备音频 - 设备ID: ${deviceId}, 尝试 ${attempt}/${maxRetries}`);
        
        // 确保MQTT连接已建立
        if (!mqttService.isServiceInitialized()) {
          console.log(`AudioManager: [订阅重试] MQTT服务未初始化，开始初始化...`);
          await mqttService.connect();
          console.log(`AudioManager: [订阅重试] MQTT服务初始化完成`);
        } else {
          console.log(`AudioManager: [订阅重试] MQTT服务已初始化`);
        }
        
        // 订阅设备音频
        console.log(`AudioManager: [订阅重试] 调用 mqttService.subscribeToDeviceAudio(${deviceId})...`);
        await mqttService.subscribeToDeviceAudio(deviceId);
        console.log(`AudioManager: [订阅重试] mqttService.subscribeToDeviceAudio() 调用完成`);
        
        // 验证订阅是否成功
        await new Promise(resolve => setTimeout(resolve, 500)); // 等待订阅完成
        const verified = await mqttService.verifyDeviceAudioSubscription(deviceId);
        
        if (verified) {
          console.log(`✅ AudioManager: 设备音频订阅成功并已验证 - 设备ID: ${deviceId}`);
          return true;
        } else {
          console.warn(`⚠️ AudioManager: 订阅验证失败 - 设备ID: ${deviceId}, 尝试 ${attempt}/${maxRetries}`);
          if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt)); // 递增延迟重试
          }
        }
      } catch (error) {
        console.error(`❌ AudioManager: 订阅设备音频失败 - 设备ID: ${deviceId}, 尝试 ${attempt}/${maxRetries}:`, error);
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt)); // 递增延迟重试
        } else {
          throw error;
        }
      }
    }
    
    console.error(`❌ AudioManager: 设备音频订阅失败（已重试${maxRetries}次） - 设备ID: ${deviceId}`);
    return false;
  }

  /**
   * 重置音频数据接收超时定时器
   */
  private resetAudioDataReceivedTimeout(): void {
    if (this.audioDataReceivedTimeout) {
      clearTimeout(this.audioDataReceivedTimeout);
    }
    
    if (this.activeDeviceId && this.state.isListening) {
      this.audioDataReceivedTimeout = setTimeout(() => {
        console.warn(`⚠️ AudioManager: 长时间未收到音频数据（${this.AUDIO_DATA_TIMEOUT_MS}ms），请检查MQTT订阅状态 - 设备ID: ${this.activeDeviceId}`);
        // 可以在这里触发一个事件，让UI显示警告
        this.emit('error', new Error(`长时间未收到音频数据，请检查MQTT订阅状态`));
      }, this.AUDIO_DATA_TIMEOUT_MS);
    }
  }
  
  /**
   * 开始监听设备
   */
  async startListeningDevice(deviceId: string): Promise<void> {
    try {
      console.log(`AudioManager: startListeningDevice 开始 - 设备ID: ${deviceId}`);
      
      // 设备与服务器心跳建立后由后端自动处理 listen，前端不再调用 POST /connections/listen
      await this.registerUserListeningDevice(deviceId);

      // 前端也需要订阅MQTT主题以接收音频数据（带重试机制）
      console.log(`AudioManager: 开始前端MQTT订阅 - 设备ID: ${deviceId}`);
      const subscriptionSuccess = await this.subscribeToDeviceAudioWithRetry(deviceId);
      if (!subscriptionSuccess) {
        console.error(`❌ AudioManager: 前端MQTT订阅失败（已重试3次） - 设备ID: ${deviceId}`);
        throw new Error(`前端MQTT订阅失败（已重试3次） - 设备ID: ${deviceId}`);
      }
      console.log(`AudioManager: 前端MQTT订阅成功 - 设备ID: ${deviceId}`);
      
      // 启动音频数据接收监控
      console.log(`AudioManager: 启动音频数据接收监控 - 设备ID: ${deviceId}`);
      this.resetAudioDataReceivedTimeout();
      
      // 更新系统状态
      console.log(`AudioManager: 设置活跃设备ID为 ${deviceId}`);
      this.activeDeviceId = deviceId; // 修复：同时更新实例变量
      this.state = {
        ...this.state,
        activeDeviceId: deviceId,
        isListening: true,
        devices: {
          ...this.state.devices,
          [deviceId]: {
            ...this.state.devices[deviceId],
            isActive: true,
            isPlaying: true
          }
        }
      };
      
      // 确保连续播放节点已初始化（如果支持AudioWorklet）
      if (this.audioContext && this.isWorkletSupported && !this.continuousPlaybackNode && !this.isInitializingAudioWorklet) {
        console.log(`AudioManager: 初始化连续播放节点 - 设备ID: ${deviceId}`);
        this.initializeContinuousPlayback().catch(error => {
          console.warn(`AudioManager: 连续播放节点初始化失败 - 设备ID: ${deviceId}:`, error);
        });
      }
      
      console.log(`AudioManager: 开始监听设备 ${deviceId}, 设置后的活跃设备ID: ${this.activeDeviceId}`);
    } catch (error) {
      console.error(`AudioManager: 开始监听设备失败 - ${deviceId}:`, error);
      throw error;
    }
  }
  
  /**
   * 向后端注册用户监听设备
   */
  private async registerUserListeningDevice(_deviceId: string): Promise<void> {
    console.log(
      'AudioManager: 不再请求 POST /connections/listen（由设备心跳与后端自动建立监听会话）'
    );
  }
  
  /**
   * 处理设备频率状态更新
   */
  private handleDeviceFrequencyStatusUpdate(parsedStatus: any): void {
    try {
      // 处理设备频率状态更新
      
      // 触发设备频率状态更新事件
      this.emit('deviceFrequencyStatusUpdate', parsedStatus);
      
      // 如果当前正在监听这个设备，更新状态
      if (this.activeDeviceId === parsedStatus.deviceId) {
        this.emit('activeDeviceFrequencyStatusUpdate', parsedStatus);
      }
      
    } catch (error) {
      console.error('AudioManager: 处理设备频率状态更新失败:', error);
    }
  }
  
  /**
   * 切换目标设备
   */
  async switchTargetDevice(deviceId: string): Promise<void> {
    try {
      console.log(`AudioManager: 开始切换设备到 ${deviceId}, 当前活跃设备: ${this.state.activeDeviceId}`);
      
      // 如果当前设备与新设备相同，确保订阅和音频播放状态正确
      // 注意：需要同时检查字符串和数字类型的比较，因为deviceId可能是字符串或数字
      const currentDeviceId = this.state.activeDeviceId;
      if (currentDeviceId === deviceId || String(currentDeviceId) === String(deviceId)) {
        console.log(`AudioManager: 设备已经是当前活跃设备，确保订阅和音频播放状态: ${deviceId} (当前设备: ${currentDeviceId})`);
        // 即使设备相同，也确保MQTT订阅、音频上下文和播放节点状态正确
        await this.ensureDeviceSubscribed(deviceId);
        return;
      }
      
      console.log(
        `AudioManager: 不再请求 POST /connections/listen/switch，仅切换前端订阅 - ${deviceId}`
      );

      const oldDeviceId = this.state.activeDeviceId;
        
        // 【修复1】清理旧设备的资源
        if (oldDeviceId && String(oldDeviceId) !== String(deviceId)) {
          console.log(`AudioManager: [切换] 开始清理旧设备资源 - 设备ID: ${oldDeviceId}`);
          
          // 清理旧设备的音频缓冲区
          if (this.audioBufferQueue.has(oldDeviceId)) {
            this.audioBufferQueue.delete(oldDeviceId);
            console.log(`AudioManager: [切换] 已清理旧设备音频缓冲区 - 设备ID: ${oldDeviceId}`);
          }
          
          // 清理旧设备的网络监控数据
          this.networkLatency.delete(oldDeviceId);
          this.packetLossRate.delete(oldDeviceId);
          this.lastPacketTime.delete(oldDeviceId);
          console.log(`AudioManager: [切换] 已清理旧设备网络监控数据 - 设备ID: ${oldDeviceId}`);
          
          // 清理旧设备的播放状态
          this.isPlaying.delete(oldDeviceId);
          this.lastPlayTime.delete(oldDeviceId);
          if (!this.hasDecodedDevicePcmListeners(String(oldDeviceId))) {
            this.clearPerDeviceVoiceSession(String(oldDeviceId));
          }
          try {
            await this.flushOpusUplinkEncoder();
          } catch (e) {
            console.warn('AudioManager: [切换] Opus 上行 flush 失败，已丢弃尾批', e);
            this.resetOpusUplinkEncoder();
          }
          console.log(`AudioManager: [切换] 已清理旧设备播放状态 - 设备ID: ${oldDeviceId}`);
        }
        
        // 【修复2】提前清空播放节点缓冲区，避免播放旧设备的音频片段
        if (this.continuousPlaybackNode) {
          try {
            console.log(`AudioManager: [切换] 清空播放节点缓冲区 - 旧设备ID: ${oldDeviceId}, 新设备ID: ${deviceId}`);
            this.continuousPlaybackNode.port.postMessage({ type: 'clear' });
            console.log(`AudioManager: [切换] 播放节点缓冲区已清空`);
          } catch (error) {
            console.warn(`AudioManager: [切换] 清空播放节点缓冲区失败:`, error);
          }
        }
        // 【修复2】等待后端事件处理完成（后端@Async事件监听器需要时间处理订阅/取消订阅）
        // 使用轮询验证方式，而不是固定等待时间，更可靠
        console.log(`AudioManager: [修复] 等待后端事件处理完成（轮询验证）...`);
        
        // 提前导入mqttService，避免重复导入
        const { mqttService } = await import('../mqttService');
        console.log(`AudioManager: [修复] MQTT服务导入成功`);
        console.log(`AudioManager: [修复] MQTT服务初始化状态: ${mqttService.isServiceInitialized()}`);
        
        // 先等待一个基础时间，让后端事件开始处理
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // 然后轮询验证订阅状态，最多等待500ms（5次，每次100ms）
        const maxPollAttempts = 5;
        const pollInterval = 100;
        let backendSubscribed = false;
        
        for (let attempt = 0; attempt < maxPollAttempts; attempt++) {
          try {
            const isSubscribed = await mqttService.verifyDeviceAudioSubscription(deviceId);
            if (isSubscribed) {
              backendSubscribed = true;
              console.log(`AudioManager: [修复] 后端订阅验证成功（第${attempt + 1}次尝试） - 设备ID: ${deviceId}`);
              break;
            }
          } catch (error) {
            console.debug(`AudioManager: [修复] 订阅验证失败（第${attempt + 1}次尝试）:`, error);
          }
          
          if (attempt < maxPollAttempts - 1) {
            await new Promise(resolve => setTimeout(resolve, pollInterval));
          }
        }
        
        if (!backendSubscribed) {
          console.log(`AudioManager: [修复] 后端订阅验证超时，继续执行前端订阅流程 - 设备ID: ${deviceId}`);
        }
        
        // 更新activeDeviceId（在清空缓冲区和等待后端事件之后）
        this.activeDeviceId = deviceId;
        console.log(`AudioManager: 已更新activeDeviceId - 从 ${oldDeviceId} 到 ${deviceId}`);
        
        // 【修复3】前端验证并补充订阅（后端可能已经订阅，但前端需要确保订阅状态正确）
        console.log(`AudioManager: [订阅检查] 开始前端MQTT订阅验证流程 - 设备ID: ${deviceId}`);
        console.log(`AudioManager: [订阅检查] 当前状态 - activeDeviceId: ${this.activeDeviceId}, isInitialized: ${this.state.isInitialized}`);
        
        try {
          
          // 取消旧设备的订阅（如果存在且与新设备不同）
          // 注意：后端已经通过ConnectionEventListener处理了取消订阅，这里作为补充确保
          if (oldDeviceId && oldDeviceId !== deviceId && String(oldDeviceId) !== String(deviceId)) {
            try {
              console.log(`AudioManager: [补充] 取消旧设备订阅 - 设备ID: ${oldDeviceId}`);
              await this.unsubscribeDeviceAudioUnlessRetained(oldDeviceId);
              console.log(`AudioManager: [补充] 前端取消订阅旧设备成功 - 设备ID: ${oldDeviceId}`);
            } catch (unsubError) {
              // 如果后端已经取消订阅，这里可能会失败，但不影响流程
              console.debug(`AudioManager: [补充] 取消旧设备订阅（可能后端已处理） - 设备ID: ${oldDeviceId}`);
            }
          }
          
          // 【修复4】先验证订阅状态，如果已订阅则跳过，避免重复订阅
          console.log(`AudioManager: [修复] 验证新设备订阅状态 - 设备ID: ${deviceId}`);
          const isAlreadySubscribed = await mqttService.verifyDeviceAudioSubscription(deviceId);
          
          if (!isAlreadySubscribed) {
            // 订阅新设备（带重试机制）
            console.log(`AudioManager: [修复] 设备未订阅，开始订阅新设备音频 - 设备ID: ${deviceId}`);
            const subscriptionSuccess = await this.subscribeToDeviceAudioWithRetry(deviceId);
            if (!subscriptionSuccess) {
              console.error(`❌ AudioManager: 前端MQTT订阅失败（已重试3次） - 设备ID: ${deviceId}`);
              // 即使订阅失败，也继续执行，但会触发音频数据接收监控警告
            } else {
              console.log(`✅ AudioManager: 前端MQTT订阅流程完成 - 设备ID: ${deviceId}`);
              
              // 【修复5】订阅后再次验证，确保订阅成功
              const verifyAfterSubscribe = await mqttService.verifyDeviceAudioSubscription(deviceId);
              if (!verifyAfterSubscribe) {
                console.warn(`⚠️ AudioManager: 订阅后验证失败，但继续执行 - 设备ID: ${deviceId}`);
              } else {
                console.log(`✅ AudioManager: 订阅后验证成功 - 设备ID: ${deviceId}`);
              }
            }
          } else {
            console.log(`✅ AudioManager: 设备已订阅（后端已处理），跳过前端订阅 - 设备ID: ${deviceId}`);
          }
        } catch (error) {
          console.error(`❌ AudioManager: 前端MQTT订阅流程异常 - 设备ID: ${deviceId}:`, error);
          // 继续执行，不阻塞设备切换
        }
        
        // 启动音频数据接收监控
        console.log(`AudioManager: 启动音频数据接收监控 - 设备ID: ${deviceId}`);
        this.resetAudioDataReceivedTimeout();
        
        // 更新系统状态
        this.state = {
          ...this.state,
          activeDeviceId: deviceId,
          isListening: true,
          devices: {
            ...this.state.devices,
            // 更新旧设备状态
            ...(oldDeviceId && String(oldDeviceId) !== String(deviceId) && {
              [oldDeviceId]: {
                ...this.state.devices[oldDeviceId],
                isActive: false,
                isPlaying: false
              }
            }),
            // 更新新设备状态
            [deviceId]: {
              ...this.state.devices[deviceId],
              isActive: true,
              isPlaying: true
            }
          }
        };
        
        // 触发设备切换事件
        this.emit('activeDeviceChanged', { 
          deviceId, 
          previousDeviceId: oldDeviceId 
        });
        
        // 确保音频上下文已恢复（设备切换后可能需要重新激活）
        try {
          if (this.audioContext && this.audioContext.state === 'suspended') {
            console.log(`AudioManager: 设备切换后恢复音频上下文 - 设备ID: ${deviceId}`);
            await this.audioContext.resume();
            console.log(`AudioManager: 音频上下文已恢复 - 状态: ${this.audioContext.state}`);
          } else if (!this.audioContext) {
            console.log(`AudioManager: 设备切换后创建音频上下文 - 设备ID: ${deviceId}`);
            await this.createAudioContext();
          }
          
          // 确保连续播放节点已初始化（设备切换后需要确保播放节点可用）
          if (this.audioContext && this.isWorkletSupported) {
            if (!this.continuousPlaybackNode && !this.isInitializingAudioWorklet) {
              console.log(`AudioManager: 设备切换后初始化连续播放节点 - 设备ID: ${deviceId}`);
              try {
                await this.initializeContinuousPlayback();
                console.log(`AudioManager: 连续播放节点初始化成功 - 设备ID: ${deviceId}`);
              } catch (error) {
                console.warn(`AudioManager: 连续播放节点初始化失败 - 设备ID: ${deviceId}:`, error);
              }
            } else if (this.continuousPlaybackNode) {
              // 播放节点已存在，确保已正确连接（缓冲区已在前面清空）
              console.log(`AudioManager: 设备切换后确保播放节点连接 - 设备ID: ${deviceId}`);
              try {
                // 重新连接播放节点到音频上下文（确保音频流正确更新）
                if (this.audioContext && this.audioContext.state === 'running') {
                  try {
                    // 先断开连接（如果已连接）
                    this.continuousPlaybackNode.disconnect();
                    // 然后重新连接到destination（确保音频流更新）
                    this.continuousPlaybackNode.connect(this.audioContext.destination);
                    console.log(`AudioManager: 播放节点已重新连接到音频上下文 - 设备ID: ${deviceId}`);
                  } catch (connectError) {
                    console.warn(`AudioManager: 重新连接播放节点失败 - 设备ID: ${deviceId}:`, connectError);
                  }
                }
              } catch (error) {
                console.warn(`AudioManager: 处理播放节点连接失败 - 设备ID: ${deviceId}:`, error);
              }
            }
          }
        } catch (error) {
          console.warn(`AudioManager: 设备切换后音频上下文处理失败 - 设备ID: ${deviceId}:`, error);
        }
      
        console.log(`AudioManager: 切换设备完成 ${deviceId}, 当前活跃设备: ${this.activeDeviceId}`);
    } catch (error) {
      console.error(`AudioManager: 切换目标设备失败 - ${deviceId}:`, error);
      throw error;
    }
  }
  
  /**
   * 独立订阅指定设备的解码后 PCM，不改变全局活跃设备，也不连接扬声器。
   * 返回的释放函数具备幂等性；最后一个独立消费者离开后才会尝试取消 MQTT 订阅。
   */
  async subscribeDecodedDevicePcm(
    deviceId: string,
    listener: DecodedDevicePcmListener,
  ): Promise<() => void> {
    const id = String(deviceId);
    let listeners = this.decodedDevicePcmListeners.get(id);
    if (!listeners) {
      listeners = new Set();
      this.decodedDevicePcmListeners.set(id, listeners);
    }
    listeners.add(listener);

    try {
      if (!this.state.isInitialized) {
        await this.initialize();
      }
      const { mqttService } = await import('../mqttService');
      if (!(await mqttService.verifyDeviceAudioSubscription(id))) {
        const subscribed = await this.subscribeToDeviceAudioWithRetry(id);
        if (!subscribed) throw new Error(`无法订阅设备 ${id} 的 Opus 音频`);
      }
    } catch (error) {
      listeners.delete(listener);
      if (listeners.size === 0) this.decodedDevicePcmListeners.delete(id);
      throw error;
    }

    let released = false;
    return () => {
      if (released) return;
      released = true;
      const current = this.decodedDevicePcmListeners.get(id);
      current?.delete(listener);
      if (current && current.size === 0) {
        this.decodedDevicePcmListeners.delete(id);
        void this.unsubscribeDeviceAudioUnlessRetained(id);
      }
    };
  }

  private hasDecodedDevicePcmListeners(deviceId: string): boolean {
    return (this.decodedDevicePcmListeners.get(String(deviceId))?.size ?? 0) > 0;
  }

  private notifyDecodedDevicePcm(deviceId: string, samples: Float32Array): void {
    const listeners = this.decodedDevicePcmListeners.get(String(deviceId));
    if (!listeners?.size) return;
    const frame: DecodedDevicePcmFrame = {
      deviceId: String(deviceId),
      samples,
      sampleRate: 48000,
      channels: 2,
      timestamp: Date.now(),
    };
    listeners.forEach((listener) => {
      try {
        listener(frame);
      } catch (error) {
        console.warn('[WaveLink audio] 解码后 PCM 消费者处理失败', error);
      }
    });
  }

  private async unsubscribeDeviceAudioUnlessRetained(
    deviceId: string,
    ignoreActiveDevice = false,
  ): Promise<void> {
    const id = String(deviceId);
    if (
      this.hasDecodedDevicePcmListeners(id) ||
      (!ignoreActiveDevice && this.activeDeviceId != null && String(this.activeDeviceId) === id)
    ) {
      return;
    }
    const { mqttService } = await import('../mqttService');
    await mqttService.unsubscribeFromDeviceAudio(id);
    this.clearPerDeviceVoiceSession(id);
  }

  /**
   * 确保设备已订阅MQTT主题（即使设备没有变化）
   * 用于页面切换回来时确保订阅状态正确
   * 同时确保音频上下文、播放节点和播放状态正确
   */
  async ensureDeviceSubscribed(deviceId: string): Promise<void> {
    try {
      console.log(`AudioManager: 确保设备订阅和音频播放状态 - 设备ID: ${deviceId}`);
      
      // 1. 确保MQTT订阅
      const { mqttService } = await import('../mqttService');
      const isSubscribed = await mqttService.verifyDeviceAudioSubscription(deviceId);
      
      if (!isSubscribed) {
        console.log(`AudioManager: 设备未订阅，开始订阅 - 设备ID: ${deviceId}`);
        const subscriptionSuccess = await this.subscribeToDeviceAudioWithRetry(deviceId);
        if (!subscriptionSuccess) {
          console.warn(`AudioManager: 设备订阅失败 - 设备ID: ${deviceId}`);
        } else {
          console.log(`AudioManager: 设备订阅成功 - 设备ID: ${deviceId}`);
        }
      } else {
        console.log(`AudioManager: 设备已订阅 - 设备ID: ${deviceId}`);
      }
      
      // 2. 确保activeDeviceId正确设置
      if (this.activeDeviceId !== deviceId && String(this.activeDeviceId) !== String(deviceId)) {
        console.log(`AudioManager: 更新活跃设备ID - 从 ${this.activeDeviceId} 到 ${deviceId}`);
        this.activeDeviceId = deviceId;
        this.state = {
          ...this.state,
          activeDeviceId: deviceId,
          isListening: true,
          devices: {
            ...this.state.devices,
            [deviceId]: {
              ...this.state.devices[deviceId],
              isActive: true,
              isPlaying: true
            }
          }
        };
      }
      
      // 3. 确保音频上下文已恢复/创建
      try {
        if (this.audioContext && this.audioContext.state === 'suspended') {
          console.log(`AudioManager: 恢复音频上下文 - 设备ID: ${deviceId}`);
          await this.audioContext.resume();
          console.log(`AudioManager: 音频上下文已恢复 - 状态: ${this.audioContext.state}`);
        } else if (!this.audioContext) {
          console.log(`AudioManager: 创建音频上下文 - 设备ID: ${deviceId}`);
          await this.createAudioContext();
        }
      } catch (error) {
        console.warn(`AudioManager: 音频上下文处理失败 - 设备ID: ${deviceId}:`, error);
      }
      
      // 4. 确保连续播放节点已初始化或重置
      if (this.audioContext && this.isWorkletSupported) {
        if (!this.continuousPlaybackNode && !this.isInitializingAudioWorklet) {
          console.log(`AudioManager: 初始化连续播放节点 - 设备ID: ${deviceId}`);
          try {
            await this.initializeContinuousPlayback();
            console.log(`AudioManager: 连续播放节点初始化成功 - 设备ID: ${deviceId}`);
          } catch (error) {
            console.warn(`AudioManager: 连续播放节点初始化失败 - 设备ID: ${deviceId}:`, error);
          }
        } else if (this.continuousPlaybackNode) {
          // 播放节点已存在，清空缓冲区（确保新设备的音频数据能正确播放）
          console.log(`AudioManager: 清空播放节点缓冲区并重新连接 - 设备ID: ${deviceId}`);
          try {
            // 清空缓冲区
            this.continuousPlaybackNode.port.postMessage({ type: 'clear' });
            console.log(`AudioManager: 播放节点缓冲区已清空 - 设备ID: ${deviceId}`);
            
            // 重新连接播放节点到音频上下文（确保音频流正确更新）
            if (this.audioContext && this.audioContext.state === 'running') {
              try {
                // 先断开连接（如果已连接）
                this.continuousPlaybackNode.disconnect();
                // 然后重新连接到destination（确保音频流更新）
                this.continuousPlaybackNode.connect(this.audioContext.destination);
                console.log(`AudioManager: 播放节点已重新连接到音频上下文 - 设备ID: ${deviceId}`);
              } catch (connectError) {
                console.warn(`AudioManager: 重新连接播放节点失败 - 设备ID: ${deviceId}:`, connectError);
              }
            }
          } catch (error) {
            console.warn(`AudioManager: 清空播放节点缓冲区失败 - 设备ID: ${deviceId}:`, error);
          }
        }
      }
      
      // 5. 启动音频数据接收监控
      this.resetAudioDataReceivedTimeout();
      
      console.log(`AudioManager: 设备订阅和音频播放状态确保完成 - 设备ID: ${deviceId}`);
    } catch (error) {
      console.error(`AudioManager: 确保设备订阅失败 - 设备ID: ${deviceId}:`, error);
    }
  }
  
  /**
   * 停止监听设备
   */
  async stopListeningDevice(deviceId: string): Promise<void> {
    try {
      // 先向后端取消注册用户监听设备（在token失效前）
      await this.unregisterUserListeningDevice(deviceId);
      
      // 前端也需要取消订阅MQTT主题
      try {
        await this.unsubscribeDeviceAudioUnlessRetained(deviceId, true);
        console.log(`AudioManager: 前端取消订阅成功 - 设备ID: ${deviceId}`);
      } catch (mqttError) {
        console.warn(`AudioManager: 前端取消订阅失败（不影响后端取消订阅） - 设备ID: ${deviceId}:`, mqttError);
        // 前端取消订阅失败不影响后端取消订阅，继续执行
      }
      
      // 更新系统状态
      this.state = {
        ...this.state,
        devices: {
          ...this.state.devices,
          [deviceId]: {
            ...this.state.devices[deviceId],
            isActive: false,
            isPlaying: false
          }
        }
      };
      
      // 如果这是当前活跃设备，清除活跃设备
      if (this.state.activeDeviceId === deviceId) {
        console.log(`AudioManager: 停止当前活跃设备 ${deviceId}，清除活跃设备ID`);
        await this.flushOpusUplinkEncoder();
        this.activeDeviceId = null; // 修复：同时清除实例变量
        this.state = {
          ...this.state,
          activeDeviceId: null,
          isListening: false
        };
        
        // 清除音频数据接收监控
        if (this.audioDataReceivedTimeout) {
          clearTimeout(this.audioDataReceivedTimeout);
          this.audioDataReceivedTimeout = null;
        }
        
        console.log(`AudioManager: 停止设备后，活跃设备ID: ${this.activeDeviceId}`);
      } else {
        console.log(`AudioManager: 停止非活跃设备 ${deviceId}，当前活跃设备: ${this.activeDeviceId}`);
      }
      
      console.log(`AudioManager: 停止监听设备 ${deviceId}`);
    } catch (error) {
      console.error(`AudioManager: 停止监听设备失败 - ${deviceId}:`, error);
      throw error;
    }
  }
  
  /**
   * 向后端取消注册用户监听设备
   */
  private async unregisterUserListeningDevice(deviceId: string): Promise<void> {
    console.log(
      `AudioManager: 不再请求 POST /connections/listen/stop（本地停止订阅即可）- ${deviceId}`
    );
  }
  
  /**
   * 开始 PTT 上行（V2：仅打开 publish gate，不断采集泵）
   */
  async startRecording(): Promise<void> {
    try {
      if (!this.state.activeDeviceId) {
        throw new Error('没有活跃设备，无法开始录音');
      }

      if (this.voiceGroupUplinkGate) {
        throw new Error('组内语音发送中，无法开始设备对讲');
      }

      if (!this.isAudioReady()) {
        throw new Error('音频通道未就绪，请等待音频准备完成');
      }

      const deviceId = String(this.state.activeDeviceId);
      const encOk = await this.ensureOpusUplinkEncoder(deviceId);
      if (!encOk) {
        throw new Error('Opus 编码器不可用');
      }

      this.setUplinkPublishGate(true);
      this.updatePttState(true);
      console.log('AudioManager: PTT 上行 gate 已开启 - 设备ID:', deviceId);
    } catch (error) {
      console.error('AudioManager: 开始录音失败:', error);
      throw error;
    }
  }
  
  /**
   * 使用AudioWorklet开始录音
   */
  private async startWorkletRecording(): Promise<void> {
    try {
      if (!this.microphoneModule) {
        throw new Error('麦克风模块未初始化');
      }

      this.syncWorkletStateFromMicrophoneModule();
      
      console.log('🎤 [测试] startWorkletRecording: 开始检查麦克风模块状态');
      
      // 获取麦克风流，如果不存在则重新请求权限
      let mediaStream = await this.microphoneModule.getMediaStream();
      if (!mediaStream) {
        console.log('🎤 [测试] AudioManager: 麦克风流不存在，重新请求麦克风权限...');
        await this.microphoneModule.requestPermission();
        mediaStream = await this.microphoneModule.getMediaStream();
        if (!mediaStream) {
          throw new Error('无法获取麦克风流');
        }
      }
      
      console.log('🎤 [测试] 麦克风流获取成功:', {
        streamId: mediaStream.id,
        active: mediaStream.active,
        tracks: mediaStream.getTracks().length
      });
      
      // 使用MicrophoneModule的AudioWorkletNode，确保音频上下文一致性
      const microphoneWorkletNode = (this.microphoneModule as any).audioWorkletNode;
      console.log('🎤 [测试] MicrophoneModule AudioWorkletNode状态:', {
        hasWorkletNode: !!microphoneWorkletNode,
        workletInitialized: (this.microphoneModule as any).workletInitialized
      });
      
      if (!microphoneWorkletNode) {
        throw new Error('MicrophoneModule的AudioWorkletNode未初始化');
      }
      
      // 获取MicrophoneModule的音频源
      const microphoneSource = this.microphoneModule.getAudioSource();
      console.log('🎤 [测试] MicrophoneModule音频源状态:', {
        hasAudioSource: !!microphoneSource
      });
      
      if (!microphoneSource) {
        throw new Error('MicrophoneModule的音频源未初始化');
      }
      
      // 连接到MicrophoneModule的AudioWorkletNode
      microphoneSource.connect(microphoneWorkletNode);
      
      console.log('🎤 [测试] AudioManager: 使用MicrophoneModule的AudioWorklet录音已开始');
      
    } catch (error) {
      console.error('AudioManager: AudioWorklet录音启动失败:', error);
      throw error;
    }
  }
  
  /**
   * 使用麦克风模块开始录音
   */
  private async startMicrophoneRecording(): Promise<void> {
    try {
      if (!this.microphoneModule) {
        throw new Error('麦克风模块未初始化');
      }
      
      // 请求麦克风权限并开始录音
      await this.microphoneModule.requestPermission();
      
      console.log('AudioManager: 麦克风模块录音已开始');
      
    } catch (error) {
      console.error('AudioManager: 麦克风模块录音启动失败:', error);
      throw error;
    }
  }
  
  /**
   * 停止 PTT 上行（V2：关 gate + flush 尾包，保留采集泵与 encoder）
   */
  async stopRecording(): Promise<void> {
    try {
      console.log('AudioManager: 停止 PTT 上行');

      this.setUplinkPublishGate(false);
      this.updatePttState(false);

      await new Promise<void>((r) => queueMicrotask(r));
      await this.flushOpusUplinkTailForPtt();

      console.log('AudioManager: PTT 上行已停止');
    } catch (error) {
      console.error('AudioManager: 停止录音失败:', error);
    }
  }

  /** 用户组上行是否正在发布 MQTT */
  isVoiceGroupUplinkActive(): boolean {
    return this.voiceGroupUplinkGate;
  }

  /**
   * 开始用户组上行（V2 gate 模型：须 AUDIO_READY，仅开 gate + 发到 voice-group/.../upstream）
   */
  async startVoiceGroupUplink(groupNo: string, userId: string): Promise<void> {
    if (!groupNo || !userId) {
      throw new Error('组号或用户 ID 无效');
    }
    if (this.uplinkPublishGate || this.state.isRecording) {
      throw new Error('设备对讲进行中，无法开始组内语音');
    }
    if (!this.isAudioReady()) {
      throw new Error('音频通道未就绪，请等待音频准备完成');
    }

    const scopeKey = AudioManager.voiceGroupEncoderScopeKey(groupNo);
    const encOk = await this.ensureOpusUplinkEncoder(scopeKey);
    if (!encOk) {
      throw new Error('Opus 编码器不可用');
    }

    this.voiceGroupEncoderScopeKey = scopeKey;
    this.voiceGroupUplinkTopic = `voice-group/${groupNo}/${userId}/audio/upstream`;
    this.voiceGroupUplinkGate = true;
    console.log('AudioManager: 用户组上行 gate 已开启', {
      groupNo,
      topic: this.voiceGroupUplinkTopic,
    });
  }

  /**
   * 停止用户组上行：关 gate + flush 尾包，保留采集泵与 encoder
   */
  async stopVoiceGroupUplink(): Promise<void> {
    if (!this.voiceGroupUplinkGate && !this.voiceGroupUplinkTopic) {
      return;
    }
    console.log('AudioManager: 停止用户组上行');
    this.voiceGroupUplinkGate = false;
    await new Promise<void>((r) => queueMicrotask(r));
    await this.flushVoiceGroupOpusUplinkTail();
    this.voiceGroupUplinkTopic = null;
  }
  
  /**
   * 停止AudioWorklet录音
   */
  private stopWorkletRecording(): void {
    try {
      if (this.microphoneModule) {
        // 获取MicrophoneModule的音频源和AudioWorkletNode
        const microphoneSource = this.microphoneModule.getAudioSource();
        const microphoneWorkletNode = (this.microphoneModule as any).audioWorkletNode;
        
        if (microphoneSource && microphoneWorkletNode) {
          // 断开录音连接
          microphoneSource.disconnect(microphoneWorkletNode);
          
          // 重新连接到分析器（保持音频电平监测）
          microphoneSource.connect((this.microphoneModule as any).analyser);
          
          console.log('AudioManager: MicrophoneModule的AudioWorklet录音已停止');
        }
      }
    } catch (error) {
      console.error('AudioManager: 停止AudioWorklet录音失败:', error);
    }
  }
  
  /**
   * 暴露 MicrophoneModule，供用户组等辅助消费者订阅 `audioData` 事件。
   * 实际数据流：MicrophoneModule(AudioWorklet) → emit `audioData`（48kHz / stereo / s16le / 20ms 对齐）。
   */
  getMicrophoneModule(): MicrophoneModule | null {
    return this.microphoneModule;
  }

  /**
   * 借用 MicrophoneModule 的 AudioWorklet 录音管线（与 171 设备 PTT 完全相同的采集/前处理）。
   *
   * - 必要时初始化 AudioContext / MicrophoneModule / AudioWorklet 与麦克风权限；
   * - 第一个借用者负责把 source → workletNode 接通（让 worklet 真正泵数据）；
   * - 与设备录音 `startRecording()` 互斥：当 `state.isRecording === true` 时返回 null；
   * - 调用方通过 `getMicrophoneModule()` 拿到 MicrophoneModule 后订阅 `audioData` 事件，
   *   再喂给业务侧的 `OpusWebCodecsUpstreamEncoder`。
   *
   * 用完务必调用 {@link releaseAuxiliaryMicrophonePump}。
   */
  async acquireAuxiliaryMicrophonePump(): Promise<MicrophoneModule | null> {
    try {
      if (!this.state.isInitialized || !this.microphoneModule || !this.audioContext) {
        await this.initialize();
      }
    } catch (error) {
      console.error('AudioManager: 辅助录音管线初始化失败:', error);
      return null;
    }

    if (!this.microphoneModule || !this.audioContext) {
      return null;
    }

    if (this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume();
      } catch (error) {
        console.warn('AudioManager: 恢复 AudioContext 失败:', error);
      }
    }

    if (this.state.isRecording || this.voiceGroupUplinkGate) {
      console.warn('AudioManager: 已有设备录音或用户组上行在进行，辅助录音管线无法启动');
      return null;
    }

    this.syncWorkletStateFromMicrophoneModule();
    if (!this.workletInitialized) {
      try {
        await this.microphoneModule.initializeAudioWorklet();
        this.syncWorkletStateFromMicrophoneModule();
      } catch (error) {
        console.error('AudioManager: AudioWorklet 初始化失败:', error);
        return null;
      }
    }

    let stream = this.microphoneModule.getMediaStream();
    if (!stream) {
      try {
        await this.microphoneModule.requestPermission();
      } catch (error) {
        console.error('AudioManager: 麦克风权限请求失败:', error);
        return null;
      }
      stream = this.microphoneModule.getMediaStream();
      if (!stream) {
        return null;
      }
    }

    const isFirstConsumer = this._auxMicPumpConsumerCount === 0;
    this._auxMicPumpConsumerCount += 1;

    if (isFirstConsumer) {
      const microphoneSource = this.microphoneModule.getAudioSource();
      const microphoneWorkletNode = (this.microphoneModule as any).audioWorkletNode as
        | AudioWorkletNode
        | null;
      if (microphoneSource && microphoneWorkletNode) {
        try {
          microphoneSource.connect(microphoneWorkletNode);
        } catch (error) {
          console.warn('AudioManager: 辅助录音 source→worklet 连接失败:', error);
        }
      }
    }

    return this.microphoneModule;
  }

  /**
   * 释放借用的辅助录音管线。最后一个释放者会断开 source→workletNode，并重连到 analyser。
   */
  releaseAuxiliaryMicrophonePump(): void {
    if (this._auxMicPumpConsumerCount <= 0) {
      this._auxMicPumpConsumerCount = 0;
      return;
    }
    this._auxMicPumpConsumerCount -= 1;
    if (this._auxMicPumpConsumerCount > 0 || !this.microphoneModule) {
      return;
    }
    if (this.capturePumpPermanent) {
      return;
    }
    const microphoneSource = this.microphoneModule.getAudioSource();
    const microphoneWorkletNode = (this.microphoneModule as any).audioWorkletNode as
      | AudioWorkletNode
      | null;
    const analyser = (this.microphoneModule as any).analyser as AnalyserNode | null;
    if (microphoneSource && microphoneWorkletNode) {
      try {
        microphoneSource.disconnect(microphoneWorkletNode);
      } catch {
        /* ignore */
      }
    }
    if (microphoneSource && analyser) {
      try {
        microphoneSource.connect(analyser);
      } catch {
        /* ignore */
      }
    }
  }

  /**
   * 获取当前活跃设备
   */
  getActiveDevice(): string | null {
    return this.activeDeviceId;
  }
  
  /**
   * 获取音频电平
   */
  getAudioLevel(): number {
    if (!this.microphoneModule) {
      return 0;
    }
    return this.microphoneModule.getAudioLevel();
  }

  /** 保存并应用上行 MIC 增益（个人信息滑块） */
  applyUplinkMicGain(gain: number): void {
    const normalized = clampUplinkMicGain(gain);
    settingsService.updateAudioSettings({ uplinkMicGain: normalized });
    this.microphoneModule?.setUplinkLinearGain(normalized);
  }
  
  /**
   * 获取系统状态
   */
  getState(): AudioSystemState {
    return { ...this.state };
  }
  
  /**
   * 恢复音频上下文（处理autoplay政策）
   */
  async resumeAudioContext(): Promise<void> {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume();
        // 音频上下文已恢复
      } catch (error) {
        console.error('AudioManager: 恢复音频上下文失败:', error);
        throw error;
      }
    }
  }

  /**
   * 在 pointer/touch 事件的同步阶段调用（不要在其前先 await 其它异步）。
   * iOS WebKit：用户激活在 await 网络请求等之后会失效，若之后才 resume，AudioContext 会长期 suspended，Worklet 不跑、无上行音频。
   * 桌面 Chrome 设备模拟仍是桌面策略，故「模拟 iPhone」正常、真机异常。
   */
  kickAudioContextOnUserGesture(): void {
    if (!this.audioContext) {
      return;
    }
    if (this.audioContext.state === 'suspended') {
      void this.audioContext.resume().catch((err) => {
        console.warn('AudioManager: 用户手势内同步 resume 未成功，后续 prepare 将重试:', err);
      });
    }
  }

  /**
   * 在发送 PTT MQTT 指令之前调用：在用户手势内 resume AudioContext。
   * 通道预热由进页 beginPipelineWarming 负责，此处不再触发 warm。
   */
  async preparePttAudioOnUserGesture(): Promise<void> {
    if (!this.state.isInitialized) {
      console.warn('AudioManager: preparePttAudioOnUserGesture 跳过（音频未初始化）');
      return;
    }
    this.kickAudioContextOnUserGesture();
    try {
      await this.resumeAudioContext();
    } catch (e) {
      console.warn('AudioManager: preparePttAudioOnUserGesture resume 未完成', e);
    }
  }

  /**
   * 播放接收到的音频数据 - 优化版本，直接使用UserAudioManager的连续播放
   */
  async playReceivedAudio(
    deviceId: string,
    audioData: ArrayBuffer,
    downstreamKind?: MqttAudioDownstreamKind
  ): Promise<void> {
    try {
      // 在PTT录音期间，不播放接收到的音频数据（半双工通信）
      if (this.uplinkPublishGate || this.state.isRecording) {
        console.log('AudioManager: PTT录音期间，跳过播放接收到的音频数据');
        return;
      }

      if (!this.audioContext) {
        await this.createAudioContext();
      }

      if (!this.audioContext) {
        throw new Error('音频上下文初始化失败');
      }

      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      const kind: MqttAudioDownstreamKind =
        downstreamKind ?? (isVoiceDownstreamOpus() ? 'opus' : 'pcm');

      if (kind === 'opus') {
        await this.deliverOpusDownstreamAsPcm(String(deviceId), audioData);
        return;
      }

      if (this.hasDecodedDevicePcmListeners(String(deviceId))) {
        const samples = this.convertAudioToFloat32Array(audioData);
        if (samples) this.notifyDecodedDevicePcm(String(deviceId), samples);
      }

      // 优化：直接使用连续播放，避免缓冲延迟
      // 跳过缓冲队列，直接使用AudioWorklet连续播放
      // 使用异步但不等待，避免阻塞
      this.playAudioContinuously(audioData).catch(error => {
        console.error('AudioManager: 连续播放失败', error);
        // 回退到原始播放方式
        this.playRawAudioData(audioData);
      });
      
      // 添加调试日志（降低频率）
      if (Math.random() < 0.01) { // 只记录1%的音频数据
        console.debug(`AudioManager: 音频播放已触发 - 设备ID: ${deviceId}, 数据大小: ${audioData.byteLength}字节`);
      }
      
    } catch (error) {
      console.error(`AudioManager: 播放设备${deviceId}音频失败:`, error);
      // 如果失败，尝试直接播放原始数据
      this.playRawAudioData(audioData);
    }
  }

  private clearPerDeviceVoiceSession(deviceId: string): void {
    const id = String(deviceId);
    const opus = this.opusDownstreamSessions.get(id);
    if (opus) {
      opus.close();
      this.opusDownstreamSessions.delete(id);
    }
  }

  /**
   * Opus 下行（audio/device/…/downstream/opus）：载荷为 **u16le 帧长 + 裸 Opus**（可多帧/包），
   * 或回退为整包一帧；WebCodecs 解码为 48 kHz mono → 立体声 float → 连续播放 Worklet。
   */
  private async deliverOpusDownstreamAsPcm(deviceId: string, mqttPayload: ArrayBuffer): Promise<void> {
    await this.deliverOpusDownstreamInternal(deviceId, mqttPayload);
  }

  private normalizeVoiceGroupSessionKey(sessionKey: string): string {
    const prefix = AudioManager.VOICE_GROUP_SESSION_PREFIX;
    return sessionKey.startsWith(prefix) ? sessionKey : `${prefix}${sessionKey}`;
  }

  private async deliverOpusDownstreamInternal(
    deviceId: string,
    mqttPayload: ArrayBuffer,
    voiceGroupOpts?: { packetSeq: number; receiveTs: number },
    shouldPlay = true,
  ): Promise<void> {
    const id = String(deviceId);

    let session = this.opusDownstreamSessions.get(id);
    if (!session) {
      session = new OpusDownstreamSession(id);
      this.opusDownstreamSessions.set(id, session);
    }
    const ok = await session.ensureConfigured();
    if (!ok) {
      console.warn('[WaveLink Opus downstream] WebCodecs 与 WASM 均不可用，跳过', { deviceId: id });
      return;
    }

    const packets = splitOpusMqttPayload(new Uint8Array(mqttPayload));
    if (packets.length === 0) {
      return;
    }

    const receiveTs = voiceGroupOpts?.receiveTs
      ?? (typeof performance !== 'undefined' ? performance.now() : Date.now());

    if (voiceGroupOpts && (
      process.env.NODE_ENV === 'development' ||
      process.env.REACT_APP_DEBUG_GROUP_AUDIO_DOWNSTREAM === '1'
    )) {
      const { packetSeq } = voiceGroupOpts;
      if (packetSeq === 1 || packetSeq % 25 === 0) {
        console.warn('[WaveLink 用户组音频下行] 拆包', {
          packetSeq,
          opusFrameCount: packets.length,
          byteLength: mqttPayload.byteLength,
          msSinceMqttReceive: Math.round(
            (typeof performance !== 'undefined' ? performance.now() : Date.now()) - receiveTs,
          ),
        });
      }
    }

    for (let i = 0; i < packets.length; i++) {
      try {
        const decodeStart = typeof performance !== 'undefined' ? performance.now() : Date.now();
        const stereo = await session.decodePacket(packets[i]);
        const decodeMs = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - decodeStart;
        if (!voiceGroupOpts && this.hasDecodedDevicePcmListeners(id)) {
          this.notifyDecodedDevicePcm(id, stereo);
        }
        if (shouldPlay) {
          await this.enqueueStereoFloat32ContinuousPlayback(stereo, voiceGroupOpts ? {
            voiceGroupPacketSeq: voiceGroupOpts.packetSeq,
            sessionKey: id,
            receiveTs,
            decodeMs,
          } : undefined);
        }
      } catch (e) {
        console.warn('[WaveLink Opus downstream] 解码失败，已重置该设备会话', { deviceId: id, err: e });
        session.close();
        this.opusDownstreamSessions.delete(id);
        break;
      }
    }
  }

  /**
   * 确保主 AudioContext 与连续播放 Worklet 就绪（171 详情页与用户组下行共用）。
   */
  async ensureVoicePlaybackReady(): Promise<void> {
    if (!this.state.isInitialized || !this.microphoneModule) {
      await this.initialize();
    }
    await this.ensureContinuousPlaybackReady();
  }

  /**
   * 用户组下行 Opus：与设备对讲相同 — decode 后直接入 Worklet 环缓（Phase 0：已移除 stale 丢弃与积压 clear）。
   */
  async playVoiceGroupDownstreamOpus(
    sessionKey: string,
    mqttPayload: ArrayBuffer | Uint8Array,
    packetSeq: number,
    receiveTs: number,
  ): Promise<void> {
    const key = this.normalizeVoiceGroupSessionKey(sessionKey);
    await this.ensureVoicePlaybackReady();
    const buf = mqttPayload instanceof Uint8Array
      ? (mqttPayload.buffer.slice(
        mqttPayload.byteOffset,
        mqttPayload.byteOffset + mqttPayload.byteLength,
      ) as ArrayBuffer)
      : mqttPayload;
    await this.deliverOpusDownstreamInternal(key, buf, { packetSeq, receiveTs });
  }

  /** 清理用户组下行 Opus session，并清空 Worklet 环缓（切组/关面板时调用） */
  clearVoiceGroupPlayback(sessionKeyPrefix: string = AudioManager.VOICE_GROUP_SESSION_PREFIX): void {
    Array.from(this.opusDownstreamSessions.keys()).forEach((id) => {
      if (id.startsWith(sessionKeyPrefix)) {
        this.clearPerDeviceVoiceSession(id);
      }
    });
    this.clearContinuousPlaybackRingBuffer();
  }

  private clearContinuousPlaybackRingBuffer(): void {
    if (!this.continuousPlaybackNode) {
      this.continuousPlaybackAvailableSamples = 0;
      return;
    }
    try {
      this.continuousPlaybackNode.port.postMessage({ type: 'clear' });
      this.continuousPlaybackAvailableSamples = 0;
    } catch {
      // 节点已断开时忽略
    }
  }

  private async ensureContinuousPlaybackReady(): Promise<boolean> {
    if (!this.audioContext) {
      await this.createAudioContext();
    }
    if (!this.audioContext) {
      return false;
    }
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
    if (!this.isWorkletSupported) {
      this.isWorkletSupported = !!(this.audioContext && 'audioWorklet' in this.audioContext);
    }
    if (!this.continuousPlaybackNode && this.isWorkletSupported) {
      if (!this.isInitializingAudioWorklet) {
        this.isInitializingAudioWorklet = true;
        this.audioWorkletInitPromise = this.initializeContinuousPlayback();
      }
      if (this.audioWorkletInitPromise) {
        try {
          await this.audioWorkletInitPromise;
        } catch (error) {
          console.error('AudioManager: AudioWorklet初始化失败', error);
          this.isWorkletSupported = false;
          return false;
        }
      }
    }
    return !!(this.isWorkletSupported && this.continuousPlaybackNode);
  }

  /**
   * 将已是 48 kHz 交错立体声 float（LRLR…）送入主上下文连续播放 Worklet（与 playAudioContinuously 一致，省略 s16 转换）。
   */
  private async enqueueStereoFloat32ContinuousPlayback(
    interleavedStereo: Float32Array,
    voiceGroupTelemetry?: {
      voiceGroupPacketSeq: number;
      sessionKey: string;
      receiveTs: number;
      decodeMs: number;
    },
  ): Promise<void> {
    const ready = await this.ensureContinuousPlaybackReady();
    if (!ready || !this.continuousPlaybackNode) {
      return;
    }
    const playbackNode = this.continuousPlaybackNode;
    const workletWriteTs = typeof performance !== 'undefined' ? performance.now() : Date.now();
    try {
      const dataCopy = new Float32Array(interleavedStereo);
      playbackNode.port.postMessage({ type: 'audioData', data: dataCopy }, [dataCopy.buffer]);
      if (voiceGroupTelemetry && (
        process.env.NODE_ENV === 'development' ||
        process.env.REACT_APP_DEBUG_GROUP_AUDIO_DOWNSTREAM === '1'
      )) {
        const { voiceGroupPacketSeq, receiveTs, decodeMs } = voiceGroupTelemetry;
        if (voiceGroupPacketSeq === 1 || voiceGroupPacketSeq % 25 === 0) {
          const pipelineMs = workletWriteTs - receiveTs;
          console.warn('[WaveLink 用户组音频下行] 播放链路延迟', {
            packetSeq: voiceGroupPacketSeq,
            decodeMs: Math.round(decodeMs * 10) / 10,
            pipelineMs: Math.round(pipelineMs),
            ringSamples: this.continuousPlaybackAvailableSamples,
            ringMs: Math.round((this.continuousPlaybackAvailableSamples / 2 / 48000) * 1000),
          });
        }
      }
    } catch (error) {
      try {
        playbackNode.port.postMessage({ type: 'audioData', data: interleavedStereo });
      } catch (fallbackError) {
        console.error('AudioManager: Opus PCM 发送 Worklet 失败', fallbackError);
      }
    }
  }
  
  /**
   * 将音频数据添加到缓冲队列
   */
  private addToAudioBuffer(deviceId: string, audioData: ArrayBuffer): void {
    if (!this.audioBufferQueue.has(deviceId)) {
      this.audioBufferQueue.set(deviceId, []);
    }
    
    const queue = this.audioBufferQueue.get(deviceId)!;
    queue.push(audioData);
    
    // 计算当前队列的总时长
    const totalBytes = queue.reduce((sum, buffer) => sum + buffer.byteLength, 0);
    const totalFrames = Math.floor(totalBytes / 4); // 4字节/帧 (2声道×2字节)
    const totalDurationMs = (totalFrames / 48000) * 1000;
    
    // 设备${deviceId}音频缓冲 - 队列:${queue.length}, 时长:${totalDurationMs.toFixed(1)}ms
    
    // 智能播放策略：
    // 1. 如果达到最小时长，播放缓冲的音频
    // 2. 如果超过最大缓冲时长，强制播放避免卡顿
    // 3. 如果队列中有足够多的片段，也播放（避免等待）
    if (totalDurationMs >= this.MIN_AUDIO_DURATION_MS || 
        totalDurationMs >= this.MAX_BUFFER_DURATION_MS ||
        queue.length >= 10) { // 如果队列中有10个或更多片段，也播放
      this.playBufferedAudio(deviceId);
    }
  }
  
  /**
   * 连续播放音频数据 - 使用AudioWorklet实现无卡顿播放
   */
  private async playAudioContinuously(audioData: ArrayBuffer): Promise<void> {
    try {
      if (!this.audioContext || this.audioContext.state !== 'running') {
        this.playRawAudioData(audioData);
        return;
      }
      
      // 检查AudioWorklet支持（使用现有的isWorkletSupported）
      if (!this.isWorkletSupported) {
        this.isWorkletSupported = !!(this.audioContext && 'audioWorklet' in this.audioContext);
      }
      
      // 确保AudioWorklet已初始化
      if (!this.continuousPlaybackNode && this.isWorkletSupported) {
        if (!this.isInitializingAudioWorklet) {
          this.isInitializingAudioWorklet = true;
          this.audioWorkletInitPromise = this.initializeContinuousPlayback();
        }
        
        if (this.audioWorkletInitPromise) {
          try {
            await this.audioWorkletInitPromise;
          } catch (error) {
            console.error('AudioManager: AudioWorklet初始化失败', error);
            this.isWorkletSupported = false;
            this.playRawAudioData(audioData);
            return;
          }
        }
      }
      
      // 如果AudioWorklet不可用，回退到原始播放方式
      if (!this.isWorkletSupported || !this.continuousPlaybackNode) {
        this.playRawAudioData(audioData);
        return;
      }
      
      // 将ArrayBuffer转换为Float32Array并发送到AudioWorklet
      const float32Data = this.convertAudioToFloat32Array(audioData);
      if (float32Data && this.continuousPlaybackNode) {
        try {
          const dataCopy = new Float32Array(float32Data);
          this.continuousPlaybackNode.port.postMessage({
            type: 'audioData',
            data: dataCopy
          }, [dataCopy.buffer]);
        } catch (error) {
          try {
            this.continuousPlaybackNode.port.postMessage({
              type: 'audioData',
              data: float32Data
            });
          } catch (fallbackError) {
            console.error('AudioManager: 发送音频数据到AudioWorklet失败', fallbackError);
            this.playRawAudioData(audioData);
          }
        }
      } else {
        this.playRawAudioData(audioData);
      }
      
    } catch (error) {
      console.error('AudioManager: 连续播放失败', error);
      this.playRawAudioData(audioData);
    }
  }
  
  /**
   * 初始化连续播放节点 - 使用AudioWorklet
   */
  private async initializeContinuousPlayback(): Promise<void> {
    if (!this.audioContext || this.continuousPlaybackNode || !this.isWorkletSupported) {
      return;
    }
    
    try {
      await this.audioContext.audioWorklet.addModule(`${publicAssetUrl('/audio-playback-processor.js')}?v=3`);
      
      const ringFloats = getAudioPlaybackRingBufferFloats();
      const minRingSamples = getAudioPlaybackMinInterleavedSamples();
      const aggressiveRing = isAudioPlaybackAggressiveRingStart();
      this.continuousPlaybackNode = new AudioWorkletNode(this.audioContext, 'audio-playback-processor', {
        processorOptions: {
          sampleRate: this.audioContext.sampleRate,
          channels: 2,
          ringBufferSize: ringFloats,
          minBufferInterleavedSamples: minRingSamples,
          aggressiveRingStart: aggressiveRing,
        },
        numberOfInputs: 0,
        numberOfOutputs: 1,
        channelCount: 2,
        channelCountMode: 'explicit',
        channelInterpretation: 'speakers'
      });
      
      this.continuousPlaybackNode.port.onmessage = (event) => {
        const { type, ...data } = event.data;
        if (type === 'status') {
          if (typeof data.availableSamples === 'number') {
            this.continuousPlaybackAvailableSamples = data.availableSamples;
            const waiters = this.continuousPlaybackStatusWaiters.splice(0);
            waiters.forEach((resolve) => resolve(data.availableSamples as number));
          }
          if (data.underrunCount > 0 && data.underrunCount % 100 === 0) {
            console.warn('AudioManager: AudioWorklet缓冲区不足', data);
          }
        }
      };
      
      this.continuousPlaybackNode.connect(this.audioContext.destination);
      this.isInitializingAudioWorklet = false;
      this.audioWorkletInitPromise = null;
      
      console.log('AudioManager: AudioWorklet连续播放节点已初始化');
      
    } catch (error) {
      console.error('AudioManager: 初始化AudioWorklet连续播放节点失败', error);
      this.isWorkletSupported = false;
      this.continuousPlaybackNode = null;
      this.isInitializingAudioWorklet = false;
      this.audioWorkletInitPromise = null;
      throw error;
    }
  }
  
  /**
   * 将ArrayBuffer转换为Float32Array（双声道交错格式）
   */
  private convertAudioToFloat32Array(audioData: ArrayBuffer): Float32Array | null {
    try {
      const pcm16Array = new Int16Array(audioData);
      const sampleCount = Math.floor(pcm16Array.length / 2);
      
      if (sampleCount <= 0) {
        return null;
      }
      
      const float32Array = new Float32Array(sampleCount * 2);
      const normalizationFactor = 1.0 / 32768.0;
      const length = Math.min(sampleCount, Math.floor(pcm16Array.length / 2));
      
      for (let i = 0; i < length; i++) {
        const baseIndex = i * 2;
        const outputIndex = i * 2;
        float32Array[outputIndex] = pcm16Array[baseIndex] * normalizationFactor;
        float32Array[outputIndex + 1] = pcm16Array[baseIndex + 1] * normalizationFactor;
      }
      
      if (length < sampleCount) {
        for (let i = length; i < sampleCount; i++) {
          const outputIndex = i * 2;
          float32Array[outputIndex] = 0;
          float32Array[outputIndex + 1] = 0;
        }
      }
      
      return float32Array;
    } catch (error) {
      console.error('AudioManager: 转换音频数据失败', error);
      return null;
    }
  }
  
  /**
   * 播放缓冲的音频数据
   */
  private playBufferedAudio(deviceId: string): void {
    const queue = this.audioBufferQueue.get(deviceId);
    if (!queue || queue.length === 0) {
      return;
    }
    
    // 合并所有音频数据
    const totalBytes = queue.reduce((sum, buffer) => sum + buffer.byteLength, 0);
    const mergedBuffer = new ArrayBuffer(totalBytes);
    const mergedView = new Uint8Array(mergedBuffer);
    
    let offset = 0;
    for (const buffer of queue) {
      mergedView.set(new Uint8Array(buffer), offset);
      offset += buffer.byteLength;
    }
    
    // 播放设备${deviceId}缓冲音频 - 字节:${totalBytes}, 片段:${queue.length}
    
    // 清空队列
    queue.length = 0;
    
    // 直接播放合并后的音频数据，确保连续性
    this.playRawAudioData(mergedBuffer);
    this.lastPlayTime.set(deviceId, Date.now());
  }

  /**
   * 播放原始音频数据（当解码失败时的备选方案）
   */
  private playRawAudioData(audioData: ArrayBuffer): void {
    try {
      if (!this.audioContext) {
        console.warn('AudioManager: 音频上下文未初始化，无法播放原始音频数据');
        return;
      }

      // 音频数据格式：48kHz, 16位, 双声道(交错), 小端字节序, 有符号
      const sampleRate = 48000;
      const channels = 2;
      const bytesPerSample = 2; // 16位 = 2字节
      const bytesPerFrame = bytesPerSample * channels; // 每帧4字节 (L+R)
      const totalFrames = Math.floor(audioData.byteLength / bytesPerFrame); // 帧数，每个帧包含左右声道
      
      // 音频数据: ${audioData.byteLength}字节, ${totalFrames}帧, ${sampleRate}Hz, ${channels}声道
      
      if (totalFrames === 0) {
        console.warn('AudioManager: 音频数据长度不足，无法播放');
        return;
      }
      
      // 检查音频数据内容（用于调试）
      const dataView = new DataView(audioData);
      let zeroCount = 0;
      for (let i = 0; i < Math.min(32, audioData.byteLength); i++) {
        if (dataView.getUint8(i) === 0) zeroCount++;
      }
      
      const audioBuffer = this.audioContext.createBuffer(channels, totalFrames, sampleRate);
      const leftChannel = audioBuffer.getChannelData(0);
      const rightChannel = audioBuffer.getChannelData(1);
      
      
      for (let i = 0; i < totalFrames; i++) {
        const frameOffset = i * bytesPerFrame;
        
        // 检查边界
        if (frameOffset + 3 >= audioData.byteLength) {
          console.warn(`AudioManager: 帧 ${i} 超出数据边界，停止处理`);
          break;
        }
        
        // 左声道：索引 frameOffset
        const leftSample = dataView.getInt16(frameOffset, true); // 小端序
        leftChannel[i] = leftSample / 32768.0; // 归一化到[-1, 1]
        
        // 右声道：索引 frameOffset + 2
        const rightSample = dataView.getInt16(frameOffset + 2, true); // 小端序
        rightChannel[i] = rightSample / 32768.0; // 归一化到[-1, 1]
      }
      
      // 创建音频源并播放
      const source = this.audioContext.createBufferSource();
      // 设置音频数据
      source.buffer = audioBuffer;
      source.connect(this.audioContext.destination);
      source.start();
      
      // 播放音频成功 - 时长:${audioBuffer.duration.toFixed(3)}s
      
      // 添加播放结束监听
      source.onended = () => {
        // 音频播放结束
      };
      
    } catch (error) {
      console.error('AudioManager: 播放原始音频数据失败:', error);
    }
  }
  
  /**
   * 事件监听
   */
  on<K extends keyof AudioManagerEvents>(
    event: K,
    callback: AudioManagerEvents[K]
  ): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event)!.push(callback);
  }
  
  /**
   * 移除事件监听
   */
  off<K extends keyof AudioManagerEvents>(
    event: K,
    callback: AudioManagerEvents[K]
  ): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      const index = listeners.indexOf(callback);
      if (index !== -1) {
        listeners.splice(index, 1);
      }
    }
  }
  
  /**
   * 触发事件
   */
  private emit<K extends keyof AudioManagerEvents>(
    event: K,
    ...args: any[]
  ): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(...args);
        } catch (error) {
          console.error(`AudioManager: 事件处理错误 (${event}):`, error);
        }
      });
    }
  }
  
  /**
   * 销毁资源
   */
  async dispose(): Promise<void> {
    console.log('AudioManager: 销毁资源...');

    this.deviceStatusMqttUnsub?.();
    this.deviceStatusMqttUnsub = null;

    if (
      this.state.isRecording ||
      this.opusUplinkEncoder !== null ||
      this.opusUplinkPendingRawPackets.length > 0
    ) {
      try {
        if (this.isWorkletSupported && this.audioWorkletNode) {
          this.stopWorkletRecording();
        }
        await new Promise<void>((r) => queueMicrotask(r));
        await this.flushOpusUplinkEncoder();
      } catch (e) {
        console.warn('AudioManager: dispose 时 Opus 尾包 flush 失败', e);
        this.resetOpusUplinkEncoder();
      }
    } else {
      this.resetOpusUplinkEncoder();
    }

    // 停止所有设备监听（仅清理本地状态，不调用需要认证的API）
    if (this.state.activeDeviceId) {
      try {
        const stoppedDeviceId = this.state.activeDeviceId; // 在清除前保存
        
        // 取消前端MQTT订阅
        try {
          await this.unsubscribeDeviceAudioUnlessRetained(stoppedDeviceId, true);
          console.log(`AudioManager: 前端MQTT订阅已取消 - 设备ID: ${stoppedDeviceId}`);
        } catch (mqttError) {
          console.warn(`AudioManager: 取消前端MQTT订阅失败 - 设备ID: ${stoppedDeviceId}:`, mqttError);
          // MQTT取消订阅失败不影响其他清理操作
        }
        
        // 更新系统状态
        this.activeDeviceId = null; // 修复：同时清除实例变量
        this.state = {
          ...this.state,
          devices: {
            ...this.state.devices,
            [stoppedDeviceId]: {
              ...this.state.devices[stoppedDeviceId],
              isActive: false,
              isPlaying: false
            }
          },
          activeDeviceId: null,
          isListening: false
        };
        
        console.log(`AudioManager: 已停止监听设备 ${stoppedDeviceId}（仅本地清理）`);
      } catch (error) {
        console.error('AudioManager: 停止设备监听失败:', error);
      }
    }
    
    // 录音用 AudioWorklet 节点由 MicrophoneModule 创建并在 mic.dispose 中断开，此处仅清除引用
    this.audioWorkletNode = null;
    
    // 销毁麦克风模块
    if (this.microphoneModule) {
      this.microphoneModule.dispose();
      this.microphoneModule = null;
    }
    
    // 关闭音频上下文
    if (this.audioContext) {
      try {
        await this.audioContext.close();
      } catch (error) {
        console.error('AudioManager: 关闭音频上下文失败:', error);
      }
      this.audioContext = null;
    }
    
    // 清理连续播放节点
    if (this.continuousPlaybackNode) {
      try {
        this.continuousPlaybackNode.disconnect();
        this.continuousPlaybackNode.port.postMessage({ type: 'clear' });
        this.continuousPlaybackNode = null;
        console.log('AudioManager: 连续播放节点已清理');
      } catch (error) {
        console.error('AudioManager: 清理连续播放节点失败:', error);
      }
    }

    // 清理所有定时器
    if (this.audioDataReceivedTimeout) {
      clearTimeout(this.audioDataReceivedTimeout);
      this.audioDataReceivedTimeout = null;
    }
    
    // 清理所有音频缓冲区
    this.audioBufferQueue.clear();
    this.audioSessionState = 'INIT';
    this.uplinkPublishGate = false;
    this.capturePumpPermanent = false;
    this.graphForceActivated = false;
    this.graphForceActivatedAt = 0;
    this.playbackPumpPrimed = false;
    this.opusDownstreamSessions.forEach((s) => s.close());
    this.opusDownstreamSessions.clear();
    console.log('AudioManager: 音频缓冲区已清空');
    
    // 清理网络监控数据
    this.networkLatency.clear();
    this.packetLossRate.clear();
    this.lastPacketTime.clear();
    
    // 清理播放状态
    this.isPlaying.clear();
    this.lastPlayTime.clear();
    
    // 重置AudioWorklet状态
    this.workletInitialized = false;
    this.isInitializingAudioWorklet = false;
    this.audioWorkletInitPromise = null;
    
    // 重置状态
    this.state.isInitialized = false;
    this.activeDeviceId = null;
    this.state.activeDeviceId = null;
    this.state.isConnected = false;
    this.state.isRecording = false;
    this.state.isListening = false;
    this.state.devices = {};
    
    // 清理事件监听器
    this.eventListeners.clear();
    
    console.log('AudioManager: 销毁完成');
  }


}

// 导出单例实例
export const audioManager = AudioManager.getInstance();
